import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  Package, Wrench, AlertTriangle, BarChart3, Plus, Search, X, Loader2,
  ArrowRightLeft, ArrowDownToLine, Warehouse, Building2, Tag, Scale,
  Clock, User, FileText, AlertCircle, ChevronDown, ChevronRight,
  Hash, RefreshCw, Trash2, ShieldAlert, CheckCircle2, Zap, MapPin,
  TrendingDown, ArrowRight, TriangleAlert, FileUp, FileSpreadsheet, Home, ClipboardCheck, ListTodo, Pencil
} from "lucide-react";
import TareasPanel from "../components/TareasPanel";   // Fix DO (2026-09-22)
import SelectBuscable, { coincide } from "../components/SelectBuscable";   // Fix DY (2026-09-23) · Fix EI
import axios from "axios";
import { API, useAuth } from "../contexts/AuthContext";
import { roleAllowed } from "../utils/roles";
import { formatPYGCompact, formatDate } from "../utils/format";
import { toast } from "sonner";
import { Skeleton } from "../components/ui/skeleton";
import { ImportMaterialsModal } from "./inventory/ImportMaterialsModal";
import { ImportExcelTemplateModal } from "./inventory/ImportExcelTemplateModal";
import AlquileresTab from "./inventory/AlquileresTab";
import MovimientoInventarioTab from "./inventory/MovimientoInventarioTab";
import AlmacenesDepositosTab from "./inventory/AlmacenesDepositosTab";   // Fix IC (2026-10-29)
import PorObraVista from "./inventory/PorObraVista";   // Fix II: «Por obra»
import { disponibleCentral } from "./compras/AsignacionStock";   // Fix GF (2026-10-15): lo disponible es lo del Almacén Central

// ─── CONSTANTS ────────────────────────────────────────────
// Fix BL (2026-09-08): "poder agruparlos por tipo de material: Canalizaciones,
// Conductores, Tableros, Dispositivos de protección, accionamiento y componentes
// para tableros, Mecanismos, Artefactos" (Edson, 06/09). Las seis nuevas van
// primero; las anteriores se conservan porque los materiales ya cargados las
// usan y borrarlas los dejaría sin tipo. Se reclasifican desde la ficha de cada
// material, que es una decisión de ellos y no se puede adivinar.
const MATERIAL_CATEGORIES = [
  "Canalizaciones", "Conductores", "Tableros",
  "Dispositivos de protección y accionamiento", "Mecanismos", "Artefactos",
  "Cables y Conductores", "Tableros y Protecciones", "Luminarias e Iluminación",
  "Materiales de Soporte", "Transformadores y Equipos", "EPP", "Herramientas Menores", "Otros",
];
const UNITS = ["metro", "unidad", "rollo", "kg", "litro", "caja", "par", "juego"];
const TOOL_CATEGORIES = [
  "Herramientas Eléctricas", "Herramientas de Medición", "Herramientas Manuales",
  "Equipos de Seguridad", "Equipos de Elevación", "Vehículos", "Otros",
];
const TABS = [
  { id: "tareas", label: "Tareas", icon: ListTodo },   // Fix DO (2026-09-22)
  { id: "materiales", label: "Materiales", icon: Package },
  { id: "movimientos", label: "Movimientos", icon: ArrowRightLeft },
  { id: "movimiento_inventario", label: "Pedido de Movimiento", icon: ClipboardCheck },
  // Fix IC (2026-10-29): "en Inventario de materiales podemos agregar una pestaña de «Apertura/Cierre de Almacenes y Depósitos»" (CEN).
  { id: "almacenes", label: "Apertura/Cierre de Almacenes y Depósitos", icon: Warehouse },
  { id: "herramientas", label: "Herramientas", icon: Wrench },
  { id: "contingencia", label: "Contingencia", icon: AlertTriangle },
  { id: "alquileres", label: "Alquileres", icon: Home },
];
// BLOQUE 35 PARTE B — precisión del cliente: sub-menú/gestión de Alquileres
// visible solo para roles de gestión (mismo set que el backend, alquileres.py).
const ALQUILER_TAB_ROLES = [
  "Superadministrador", "Administrador", "Gerente Tecnico", "Gerencia Técnica",
  "Coordinador de Obra", "Jefe de Compras", "Jefe de Logística",
  "Residente de Obra",   // Fix FH (2026-10-08)
];

// ─── HELPERS ──────────────────────────────────────────────
const TOOL_STATUS = {
  disponible: { label: "Disponible", cls: "bg-emerald-100 text-emerald-700" },
  asignado: { label: "Asignado", cls: "bg-blue-100 text-blue-700" },
  dañado: { label: "Dañado", cls: "bg-amber-100 text-amber-700" },
  perdido: { label: "Perdido", cls: "bg-red-100 text-red-700" },
};
const MOV_TYPE = {
  entry: { label: "Entrada", cls: "bg-emerald-100 text-emerald-700", icon: ArrowDownToLine },
  transfer: { label: "Transferencia", cls: "bg-blue-100 text-blue-700", icon: ArrowRightLeft },
  consumo: { label: "Consumo", cls: "bg-orange-100 text-orange-700", icon: TrendingDown },
  adjustment: { label: "Ajuste", cls: "bg-gray-100 text-gray-700", icon: RefreshCw },
};

// Fix EI (2026-09-27): "que se ordene por el código para mejor visualización, y también que
// permita escribir y se vaya filtrando el despliegue conforme se vaya escribiendo" (CEN).
// Orden natural: MULT-2 va antes que MULT-10.
const opcionesMaterial = (materials) => [...(materials || [])]
  .sort((a, b) => String(a.code || "").localeCompare(String(b.code || ""), "es", { numeric: true, sensitivity: "base" }))
  .map(m => ({ value: m.id, label: `${m.code} – ${m.name}${m.marca ? ` · ${m.marca}` : ""}` }));   // Fix FN: con la marca
const TOOL_EDIT_ROLES = ["Superadministrador", "Jefe de Compras", "Jefe de Logística"];
const MOV_EDIT_ROLES = ["Superadministrador", "Jefe de Logística", "Jefe de Compras"];

function stockPct(total, min) {
  if (min <= 0) return 100;
  return Math.min(200, Math.round((total / min) * 100));
}
function stockColor(pct) {
  if (pct === 0) return { bar: "bg-red-600", text: "text-red-600", bg: "bg-red-50 border-red-200" };
  if (pct <= 50) return { bar: "bg-red-500", text: "text-red-600", bg: "bg-red-50 border-red-200" };
  if (pct <= 100) return { bar: "bg-amber-500", text: "text-amber-600", bg: "bg-amber-50 border-amber-200" };
  return { bar: "bg-emerald-500", text: "text-emerald-600", bg: "bg-emerald-50 border-emerald-200" };
}
// Fix GF (2026-10-15): "que el número de cantidad disponible sea primero lo de almacén central, esa es
// la info importante a visualizar" (CEN). El Almacén Central va primero (y aparece aunque no tenga
// stock); el resto de las ubicaciones queda como estaba.
function ubicacionesCentralPrimero(locations) {
  const locs = [...(locations || [])];
  const i = locs.findIndex(l => l.location_id === "almacen_central");
  const central = i >= 0 ? locs.splice(i, 1)[0] : { location_id: "almacen_central", name: "Almacén Central", quantity: 0 };
  return [central, ...locs];
}

const inputCls = "w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4] bg-white transition-all";
const labelCls = "block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5";

// ─── MATERIAL MODAL ───────────────────────────────────────
function MaterialModal({ open, onClose, onSaved, material = null }) {
  const empty = { code: "", name: "", unit: "metro", category: MATERIAL_CATEGORIES[0], min_stock: "", unit_cost_pyg: "", marca: "", notes: "" };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) setForm(material ? { ...material, min_stock: material.min_stock ?? "", unit_cost_pyg: material.unit_cost_pyg ?? "" } : empty);
    setError("");
  }, [open, material]);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.code || !form.name || !form.unit || !form.min_stock) { setError("Complete los campos obligatorios"); return; }
    setSaving(true); setError("");
    try {
      const payload = { ...form, min_stock: Number(form.min_stock), unit_cost_pyg: Number(form.unit_cost_pyg) || 0 };
      if (material) await axios.put(`${API}/inventory/materials/${material.id}`, payload, { withCredentials: true });
      else await axios.post(`${API}/inventory/materials`, payload, { withCredentials: true });
      toast.success(material ? "Material actualizado" : "Material creado");
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="material-modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto" data-testid="material-modal">
        <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between rounded-t-2xl">
          <div><h2 className="text-base font-bold text-gray-900">{material ? "Editar Material" : "Nuevo Material"}</h2><p className="text-xs text-gray-400 mt-0.5">Registro en inventario</p></div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors" data-testid="material-modal-close"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Código *</label><input name="code" value={form.code} onChange={handleChange} placeholder="MAT-009" className={inputCls} data-testid="material-code-input" /></div>
            <div><label className={labelCls}>Unidad *</label>
              <select name="unit" value={form.unit} onChange={handleChange} className={inputCls} data-testid="material-unit-select">
                {UNITS.map(u => <option key={u}>{u}</option>)}
              </select>
            </div>
          </div>
          <div><label className={labelCls}>Nombre *</label><input name="name" value={form.name} onChange={handleChange} placeholder="Ej: Cable NYY 4x10mm" className={inputCls} data-testid="material-name-input" /></div>
          <div><label className={labelCls}>Marca</label><input name="marca" value={form.marca || ""} onChange={handleChange} placeholder="Ej: Nexans, Siemens..." className={inputCls} data-testid="material-marca-input" /></div>
          <div><label className={labelCls}>Categoría</label>
            <select name="category" value={form.category} onChange={handleChange} className={inputCls} data-testid="material-category-select">
              {/* Fix BL (2026-09-08): si el material tiene un tipo que ya no está
                  en la lista, se agrega igual para no perderlo al guardar. */}
              {(form.category && !MATERIAL_CATEGORIES.includes(form.category)
                ? [form.category, ...MATERIAL_CATEGORIES] : MATERIAL_CATEGORIES
              ).map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Stock Mínimo *</label><input name="min_stock" type="number" value={form.min_stock} onChange={handleChange} placeholder="100" className={inputCls} data-testid="material-minstock-input" /></div>
            <div><label className={labelCls}>Costo unitario (₲)</label><input name="unit_cost_pyg" type="number" value={form.unit_cost_pyg} onChange={handleChange} placeholder="28500" className={inputCls} data-testid="material-cost-input" /></div>
          </div>
          <div><label className={labelCls}>Notas</label><textarea name="notes" value={form.notes} onChange={handleChange} rows={2} placeholder="Observaciones..." className={`${inputCls} resize-none`} data-testid="material-notes-input" /></div>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2 text-sm" data-testid="material-modal-error">{error}</div>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-3 text-sm font-medium hover:bg-gray-50 transition-all" data-testid="material-modal-cancel">Cancelar</button>
            <button type="submit" disabled={saving} className="flex-1 bg-[#2E6DA4] hover:bg-[#245A8A] active:scale-[0.98] text-white rounded-xl py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-60" data-testid="material-modal-save">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
              {material ? "Guardar Cambios" : "Crear Material"}
            </button>
          </div>
        </form>
      </div>
    </div>
  , document.body);
}

// ─── ENTRY MODAL ──────────────────────────────────────────
function EntryModal({ open, onClose, onSaved, materials, almacenes }) {
  const empty = { material_id: "", quantity: "", location_dest: "almacen_central", location_dest_name: "Almacén Central", supplier: "", po_number: "", cost_unit_pyg: "", notes: "" };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const locationOptions = almacenes.length > 0 ? almacenes.map(a => ({ id: a.id, name: a.nombre })) : [{ id: "almacen_central", name: "Almacén Central" }];

  useEffect(() => { if (open) { setForm(empty); setError(""); } }, [open]);

  const handleChange = e => {
    const { name, value } = e.target;
    if (name === "location_dest") {
      const loc = locationOptions.find(l => l.id === value);
      setForm(f => ({ ...f, location_dest: value, location_dest_name: loc?.name || value }));
    } else {
      setForm(f => ({ ...f, [name]: value }));
    }
  };

  const selectedMaterial = materials.find(m => m.id === form.material_id);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.material_id || !form.quantity || !form.location_dest) { setError("Complete los campos obligatorios"); return; }
    setSaving(true); setError("");
    try {
      await axios.post(`${API}/inventory/movements/entry`, {
        ...form, quantity: Number(form.quantity), cost_unit_pyg: Number(form.cost_unit_pyg) || 0,
      }, { withCredentials: true });
      toast.success("Entrada de material registrada");
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || "Error al registrar");
    } finally { setSaving(false); }
  };

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="entry-modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto" data-testid="entry-modal">
        <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3"><div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center"><ArrowDownToLine size={16} className="text-emerald-600" /></div><div><h2 className="text-base font-bold text-gray-900">Registrar Entrada</h2><p className="text-xs text-gray-400">Ingreso de material al inventario</p></div></div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors" data-testid="entry-modal-close"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div><label className={labelCls}>Material *</label>
            <SelectBuscable value={form.material_id} onChange={v => handleChange({ target: { name: "material_id", value: v } })}
              options={opcionesMaterial(materials)} vacio="Seleccionar material..." placeholderBuscar="Buscar código o material..."
              className={inputCls} testId="entry-material-select" />
          </div>
          {selectedMaterial && (
            <div className="bg-[#EBF2F9] rounded-xl px-3 py-2 text-xs text-[#2E6DA4] font-medium">
              Stock actual: {selectedMaterial.total_stock} {selectedMaterial.unit} — Mín: {selectedMaterial.min_stock} {selectedMaterial.unit}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Cantidad *</label><input name="quantity" type="number" value={form.quantity} onChange={handleChange} placeholder="0" className={inputCls} data-testid="entry-quantity-input" /></div>
            <div><label className={labelCls}>Costo unit. (₲)</label><input name="cost_unit_pyg" type="number" value={form.cost_unit_pyg} onChange={handleChange} placeholder="0" className={inputCls} data-testid="entry-cost-input" /></div>
          </div>
          <div><label className={labelCls}>Destino *</label>
            <select name="location_dest" value={form.location_dest} onChange={handleChange} className={inputCls} data-testid="entry-location-select">
              {locationOptions.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Proveedor</label><input name="supplier" value={form.supplier} onChange={handleChange} placeholder="Nombre proveedor" className={inputCls} data-testid="entry-supplier-input" /></div>
            <div><label className={labelCls}>N° OC</label><input name="po_number" value={form.po_number} onChange={handleChange} placeholder="OC-2026-001" className={`${inputCls} font-mono`} data-testid="entry-po-input" /></div>
          </div>
          <div><label className={labelCls}>Notas</label><input name="notes" value={form.notes} onChange={handleChange} placeholder="Observaciones..." className={inputCls} data-testid="entry-notes-input" /></div>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2 text-sm" data-testid="entry-modal-error">{error}</div>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-3 text-sm font-medium hover:bg-gray-50 transition-all" data-testid="entry-modal-cancel">Cancelar</button>
            <button type="submit" disabled={saving} className="flex-1 bg-emerald-600 hover:bg-emerald-700 active:scale-[0.98] text-white rounded-xl py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-60" data-testid="entry-modal-save">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <ArrowDownToLine size={16} />}
              Registrar Entrada
            </button>
          </div>
        </form>
      </div>
    </div>
  , document.body);
}

// ─── TRANSFER MODAL ───────────────────────────────────────
function TransferModal({ open, onClose, onSaved, materials, almacenes }) {
  const empty = { material_id: "", quantity: "", from_location: "almacen_central", from_location_name: "Almacén Central", to_location: "", to_location_name: "", project_id: null, notes: "" };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const selectedMaterial = materials.find(m => m.id === form.material_id);
  const sourceLocations = selectedMaterial?.locations?.filter(l => l.quantity > 0) || [];

  const destLocations = (almacenes.length > 0 ? almacenes.map(a => ({ id: a.id, name: a.nombre, project_id: a.project_id || null }))
    : [{ id: "almacen_central", name: "Almacén Central" }]
  ).filter(l => l.id !== form.from_location);

  useEffect(() => { if (open) { setForm(empty); setError(""); } }, [open]);

  const handleChange = e => {
    const { name, value } = e.target;
    if (name === "from_location") {
      const loc = sourceLocations.find(l => l.location_id === value);
      setForm(f => ({ ...f, from_location: value, from_location_name: loc?.name || value }));
    } else if (name === "to_location") {
      const dest = destLocations.find(l => l.id === value);
      setForm(f => ({ ...f, to_location: value, to_location_name: dest?.name || value, project_id: dest?.project_id || null }));
    } else {
      setForm(f => ({ ...f, [name]: value }));
    }
  };

  const maxQty = sourceLocations.find(l => l.location_id === form.from_location)?.quantity || 0;

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.material_id || !form.quantity || !form.from_location || !form.to_location) { setError("Complete todos los campos obligatorios"); return; }
    if (Number(form.quantity) > maxQty) { setError(`Stock insuficiente. Disponible: ${maxQty}`); return; }
    if (form.from_location === form.to_location) { setError("El origen y destino no pueden ser iguales"); return; }
    setSaving(true); setError("");
    try {
      await axios.post(`${API}/inventory/movements/transfer`, {
        ...form, quantity: Number(form.quantity),
      }, { withCredentials: true });
      toast.success("Transferencia realizada. Costo cargado al centro de costos.");
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || "Error al transferir");
    } finally { setSaving(false); }
  };

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="transfer-modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto" data-testid="transfer-modal">
        <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3"><div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center"><ArrowRightLeft size={16} className="text-blue-600" /></div><div><h2 className="text-base font-bold text-gray-900">Transferir Material</h2><p className="text-xs text-gray-400">Se cargará al centro de costos del proyecto</p></div></div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors" data-testid="transfer-modal-close"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div><label className={labelCls}>Material *</label>
            <SelectBuscable value={form.material_id}
              onChange={v => { handleChange({ target: { name: "material_id", value: v } }); setForm(f => ({ ...f, from_location: "almacen_central", from_location_name: "Almacén Central" })); }}
              options={opcionesMaterial(materials)} vacio="Seleccionar material..." placeholderBuscar="Buscar código o material..."
              className={inputCls} testId="transfer-material-select" />
          </div>
          {selectedMaterial && (
            <div><label className={labelCls}>Origen *</label>
              <select name="from_location" value={form.from_location} onChange={handleChange} className={inputCls} data-testid="transfer-from-select">
                {sourceLocations.map(l => <option key={l.location_id} value={l.location_id}>{`${l.name} (${l.quantity} ${selectedMaterial.unit})`}</option>)}
              </select>
            </div>
          )}
          <div><label className={labelCls}>Destino *</label>
            <select name="to_location" value={form.to_location} onChange={handleChange} className={inputCls} data-testid="transfer-to-select">
              <option value="">Seleccionar destino...</option>
              {destLocations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
            {form.project_id && <p className="text-xs text-[#2E6DA4] mt-1.5 font-medium">El costo de materiales se cargará al presupuesto de esta obra.</p>}
          </div>
          <div><label className={labelCls}>Cantidad * {maxQty > 0 && <span className="text-gray-400 font-normal">(máx: {maxQty})</span>}</label>
            <input name="quantity" type="number" max={maxQty} value={form.quantity} onChange={handleChange} placeholder="0" className={inputCls} data-testid="transfer-quantity-input" />
          </div>
          <div><label className={labelCls}>Notas</label><input name="notes" value={form.notes} onChange={handleChange} placeholder="Motivo de la transferencia..." className={inputCls} data-testid="transfer-notes-input" /></div>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2 text-sm" data-testid="transfer-modal-error">{error}</div>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-3 text-sm font-medium hover:bg-gray-50 transition-all" data-testid="transfer-modal-cancel">Cancelar</button>
            <button type="submit" disabled={saving} className="flex-1 bg-[#2E6DA4] hover:bg-[#245A8A] active:scale-[0.98] text-white rounded-xl py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-60" data-testid="transfer-modal-save">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <ArrowRightLeft size={16} />}
              Transferir
            </button>
          </div>
        </form>
      </div>
    </div>
  , document.body);
}

// ─── ENTRADA DESDE OC MODAL (CEN 2.0 FASE 5 CHECKPOINT A) ────
function EntryFromOCModal({ open, onClose, onSaved, almacenes, materials }) {
  const [ocs, setOcs] = useState([]);
  const [ocId, setOcId] = useState("");
  const [ocDetail, setOcDetail] = useState(null);
  const [selected, setSelected] = useState({}); // item_index -> { checked, quantity, material_id, cost_unit_pyg }
  const [almacenDestino, setAlmacenDestino] = useState("almacen_central");
  const [loadingOcs, setLoadingOcs] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setOcId(""); setOcDetail(null); setSelected({}); setError("");
    setAlmacenDestino(almacenes[0]?.id || "almacen_central");
    setLoadingOcs(true);
    axios.get(`${API}/inventory/ocs-pendientes-ingreso`, { withCredentials: true })
      .then(({ data }) => setOcs(data)).catch(() => toast.error("Error cargando OCs pendientes"))
      .finally(() => setLoadingOcs(false));
  }, [open]);

  useEffect(() => {
    if (!ocId) { setOcDetail(null); return; }
    setLoadingItems(true);
    axios.get(`${API}/inventory/ocs/${ocId}/items-pendientes-ingreso`, { withCredentials: true })
      .then(({ data }) => {
        setOcDetail(data);
        const init = {};
        data.items.forEach(it => { init[it.item_index] = { checked: it.pendiente > 0, quantity: it.pendiente, material_id: it.material_id || "", cost_unit_pyg: it.unit_price || 0 }; });
        setSelected(init);
      })
      .catch(() => toast.error("Error cargando ítems de la OC"))
      .finally(() => setLoadingItems(false));
  }, [ocId]);

  const updateSel = (idx, patch) => setSelected(s => ({ ...s, [idx]: { ...s[idx], ...patch } }));

  const handleSubmit = async e => {
    e.preventDefault();
    const items = Object.entries(selected).filter(([, v]) => v.checked && Number(v.quantity) > 0).map(([idx, v]) => ({
      item_index: Number(idx), material_id: v.material_id, quantity: Number(v.quantity), cost_unit_pyg: Number(v.cost_unit_pyg) || 0,
    }));
    if (!ocId || items.length === 0) { setError("Seleccione la OC y al menos un ítem con cantidad y material"); return; }
    if (items.some(it => !it.material_id)) { setError("Todos los ítems seleccionados deben tener un material asignado"); return; }
    setSaving(true); setError("");
    try {
      const alm = almacenes.find(a => a.id === almacenDestino);
      await axios.post(`${API}/inventory/movements/entry-from-oc`, {
        oc_id: ocId, almacen_destino_id: almacenDestino, almacen_destino_nombre: alm?.nombre || "Almacén Central", items,
      }, { withCredentials: true });
      toast.success(`Entrada registrada (${items.length} ítem/s)`);
      onSaved();
    } catch (err) { setError(err.response?.data?.detail || "Error al registrar la entrada"); }
    finally { setSaving(false); }
  };

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="entry-oc-modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="entry-oc-modal">
        <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between rounded-t-2xl z-10">
          <div className="flex items-center gap-3"><div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center"><FileText size={16} className="text-teal-600" /></div><div><h2 className="text-base font-bold text-gray-900">Entrada desde OC</h2><p className="text-xs text-gray-400">Precarga materiales, cantidades y costos de una Orden de Compra</p></div></div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors" data-testid="entry-oc-modal-close"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div><label className={labelCls}>Orden de Compra *</label>
            {loadingOcs ? <Skeleton className="h-10 rounded-xl" /> : (
              <select value={ocId} onChange={e => setOcId(e.target.value)} className={inputCls} data-testid="entry-oc-select">
                <option value="">Seleccionar OC con ítems pendientes de ingreso...</option>
                {ocs.map(oc => <option key={oc.id} value={oc.id}>{oc.number} — {oc.provider_name} ({oc.items_pendientes_count} ítem/s pendientes)</option>)}
              </select>
            )}
            {!loadingOcs && ocs.length === 0 && <p className="text-xs text-gray-400 mt-1.5">No hay OCs con ítems pendientes de ingreso.</p>}
          </div>
          <div><label className={labelCls}>Almacén Destino *</label>
            <select value={almacenDestino} onChange={e => setAlmacenDestino(e.target.value)} className={inputCls} data-testid="entry-oc-almacen-select">
              {almacenes.map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
            </select>
          </div>
          {loadingItems ? <Skeleton className="h-32 rounded-xl" /> : ocDetail && (
            <div className="space-y-2" data-testid="entry-oc-items-list">
              <label className={labelCls}>Ítems a ingresar</label>
              {ocDetail.items.map(it => {
                const sel = selected[it.item_index] || {};
                return (
                  <div key={it.item_index} className={`border rounded-xl p-3 ${it.pendiente <= 0 ? "opacity-40" : ""}`} data-testid={`entry-oc-item-${it.item_index}`}>
                    <div className="flex items-start gap-3">
                      <input type="checkbox" checked={!!sel.checked} disabled={it.pendiente <= 0} onChange={e => updateSel(it.item_index, { checked: e.target.checked })} className="mt-1" data-testid={`entry-oc-item-check-${it.item_index}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800">{it.item_name}</p>
                        <p className="text-xs text-gray-400">Pedido: {it.quantity} {it.unit} · Ya ingresado: {it.qty_ingresada} · Pendiente: <span className="font-semibold text-teal-600">{it.pendiente} {it.unit}</span></p>
                        {sel.checked && (
                          <div className="grid grid-cols-3 gap-2 mt-2">
                            <SelectBuscable value={sel.material_id || ""} onChange={v => updateSel(it.item_index, { material_id: v })}
                              options={opcionesMaterial(materials)} vacio="Seleccionar material..." placeholderBuscar="Buscar código o material..."
                              className={`${inputCls} text-xs py-1.5`} testId={`entry-oc-item-material-${it.item_index}`} />
                            <input type="number" placeholder="Cantidad" max={it.pendiente} value={sel.quantity} onChange={e => updateSel(it.item_index, { quantity: e.target.value })} className={`${inputCls} text-xs py-1.5`} data-testid={`entry-oc-item-qty-${it.item_index}`} />
                            <input type="number" placeholder="Costo unit." value={sel.cost_unit_pyg} onChange={e => updateSel(it.item_index, { cost_unit_pyg: e.target.value })} className={`${inputCls} text-xs py-1.5`} data-testid={`entry-oc-item-cost-${it.item_index}`} />
                          </div>
                        )}
                        {sel.checked && !sel.material_id && <p className="text-[11px] text-amber-600 mt-1">No se resolvió automáticamente el material del catálogo — seleccione uno manualmente.</p>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2 text-sm" data-testid="entry-oc-modal-error">{error}</div>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-3 text-sm font-medium hover:bg-gray-50 transition-all" data-testid="entry-oc-modal-cancel">Cancelar</button>
            <button type="submit" disabled={saving || !ocDetail} className="flex-1 bg-teal-600 hover:bg-teal-700 active:scale-[0.98] text-white rounded-xl py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-60" data-testid="entry-oc-modal-save">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <ArrowDownToLine size={16} />}
              Registrar Entrada
            </button>
          </div>
        </form>
      </div>
    </div>
  , document.body);
}

// ─── CONSUMO MODAL (obrador -> instalado) — CEN 2.0 FASE 5 CHECKPOINT A ─────
function ConsumoModal({ open, onClose, onSaved, materials, almacenes }) {
  // Fix BB (2026-09-07): "esa salida se tiene que hacer asignando tarea y nivel"
  // (Edson, 06/09). Nivel e ítem eran campos de texto donde había que escribir el
  // identificador interno a mano, así que en la práctica el material salía sin
  // imputar y el consumo por nivel quedaba en cero.
  const empty = { material_id: "", almacen_origen_id: "", quantity: "", nivel_id: "", item_uid: "", tarea_id: "", notes: "" };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [ctx, setCtx] = useState({ niveles: [], items: [], tareas: [], obra_nombre: "" });

  const obraAlmacenes = almacenes.filter(a => a.obra_id);
  const selectedMaterial = materials.find(m => m.id === form.material_id);
  const selectedAlmacen = obraAlmacenes.find(a => a.id === form.almacen_origen_id);
  const availableQty = selectedMaterial?.locations?.find(l => l.location_id === form.almacen_origen_id)?.quantity || 0;

  useEffect(() => { if (open) { setForm(empty); setError(""); } }, [open]);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  // Fix BB (2026-09-07): el almacén de origen ya dice de qué obra se trata, así
  // que las listas de niveles, ítems y tareas se cargan solas.
  useEffect(() => {
    const obraId = selectedAlmacen?.obra_id;
    if (!open || !obraId) { setCtx({ niveles: [], items: [], tareas: [], obra_nombre: "" }); return; }
    let vigente = true;
    axios.get(`${API}/inventory/obra-contexto/${obraId}`, { withCredentials: true })
      .then(({ data }) => { if (vigente) setCtx({ niveles: data.niveles || [], items: data.items || [], tareas: data.tareas || [], obra_nombre: data.obra_nombre || "" }); })
      .catch(() => { if (vigente) setCtx({ niveles: [], items: [], tareas: [], obra_nombre: "" }); });
    return () => { vigente = false; };
  }, [open, selectedAlmacen?.obra_id]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Las tareas del ítem elegido primero; sin ítem elegido, todas las de la obra.
  const tareasVisibles = form.item_uid ? ctx.tareas.filter(t => t.item_uid === form.item_uid) : ctx.tareas;

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.material_id || !form.almacen_origen_id || !form.quantity) { setError("Complete los campos obligatorios"); return; }
    if (Number(form.quantity) > availableQty) { setError(`Stock insuficiente en el origen. Disponible: ${availableQty}`); return; }
    setSaving(true); setError("");
    try {
      const tarea = ctx.tareas.find(t => t.id === form.tarea_id);
      await axios.post(`${API}/inventory/movements/consumo`, {
        material_id: form.material_id, almacen_origen_id: form.almacen_origen_id, quantity: Number(form.quantity),
        obra_id: selectedAlmacen?.obra_id, nivel_id: form.nivel_id || null, item_uid: form.item_uid || null,
        tarea_id: form.tarea_id || null, tarea_nombre: tarea ? `${tarea.tarea_numero || ""} ${tarea.tarea_nombre || ""}`.trim() : "",
        notes: form.notes,
      }, { withCredentials: true });
      toast.success("Consumo registrado — material instalado en obra");
      onSaved();
    } catch (err) { setError(err.response?.data?.detail || "Error al registrar el consumo"); }
    finally { setSaving(false); }
  };

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="consumo-modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto" data-testid="consumo-modal">
        <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3"><div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center"><TrendingDown size={16} className="text-orange-600" /></div><div><h2 className="text-base font-bold text-gray-900">Registrar Consumo</h2><p className="text-xs text-gray-400">Material instalado en obra (sale del inventario)</p></div></div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors" data-testid="consumo-modal-close"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div><label className={labelCls}>Material *</label>
            <SelectBuscable value={form.material_id} onChange={v => handleChange({ target: { name: "material_id", value: v } })}
              options={opcionesMaterial(materials)} vacio="Seleccionar material..." placeholderBuscar="Buscar código o material..."
              className={inputCls} testId="consumo-material-select" />
          </div>
          <div><label className={labelCls}>Almacén de Origen (Obrador/Depósito) *</label>
            <select name="almacen_origen_id" value={form.almacen_origen_id} onChange={handleChange} className={inputCls} data-testid="consumo-almacen-select">
              <option value="">Seleccionar almacén...</option>
              {/* Fix BC (2026-09-07): alfabético, para que las obras del mismo
                  nombre queden juntas y se lean sus códigos de corrido. */}
              {[...obraAlmacenes].sort((a, b) => (a.nombre || "").localeCompare(b.nombre || "", "es"))
                .map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
            </select>
          </div>
          {selectedMaterial && form.almacen_origen_id && (
            <div className="bg-orange-50 rounded-xl px-3 py-2 text-xs text-orange-700 font-medium">Disponible en este almacén: {availableQty} {selectedMaterial.unit}</div>
          )}
          <div><label className={labelCls}>Cantidad Consumida *</label><input name="quantity" type="number" value={form.quantity} onChange={handleChange} placeholder="0" className={inputCls} data-testid="consumo-quantity-input" /></div>
          {/* Fix BB (2026-09-07): ítem, nivel y tarea se eligen del presupuesto de
              la obra. Sin esto el material salía del depósito sin imputar y el
              consumo por nivel de la obra quedaba en cero. */}
          <div><label className={labelCls}>Ítem del presupuesto</label>
            <select name="item_uid" value={form.item_uid} onChange={e => setForm(f => ({ ...f, item_uid: e.target.value, tarea_id: "" }))}
              className={inputCls} disabled={!selectedAlmacen} data-testid="consumo-item-select">
              <option value="">{selectedAlmacen ? "— Sin imputar —" : "Elegí primero el almacén"}</option>
              {ctx.items.map(it => (
                <option key={it.item_uid} value={it.item_uid}>
                  {`${it.numero ? it.numero + " " : ""}${it.descripcion}${it.seccion ? "  ·  " + it.seccion : ""}`}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Nivel</label>
              <select name="nivel_id" value={form.nivel_id} onChange={handleChange} className={inputCls}
                disabled={!selectedAlmacen} data-testid="consumo-nivel-select">
                <option value="">{ctx.niveles.length > 0 ? "— Sin nivel —" : "Sin niveles cargados"}</option>
                {ctx.niveles.map(n => <option key={n.id} value={n.id}>{n.label}</option>)}
              </select>
            </div>
            <div><label className={labelCls}>Tarea</label>
              <select name="tarea_id" value={form.tarea_id} onChange={handleChange} className={inputCls}
                disabled={!selectedAlmacen} data-testid="consumo-tarea-select">
                <option value="">{tareasVisibles.length > 0 ? "— Sin tarea —" : "Sin tareas generadas"}</option>
                {tareasVisibles.map(t => (
                  <option key={t.id} value={t.id}>
                    {`${t.tarea_numero ? t.tarea_numero + " " : ""}${t.tarea_nombre}${t.nivel_label ? "  ·  " + t.nivel_label : ""}`}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {selectedAlmacen && ctx.items.length === 0 && (
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="consumo-sin-presupuesto">
              Esta obra no tiene un presupuesto vinculado, así que no hay ítems ni niveles para imputar. El consumo se registra igual.
            </p>
          )}
          <div><label className={labelCls}>Notas</label><input name="notes" value={form.notes} onChange={handleChange} placeholder="Observaciones..." className={inputCls} data-testid="consumo-notes-input" /></div>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2 text-sm" data-testid="consumo-modal-error">{error}</div>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-3 text-sm font-medium hover:bg-gray-50 transition-all" data-testid="consumo-modal-cancel">Cancelar</button>
            <button type="submit" disabled={saving} className="flex-1 bg-orange-600 hover:bg-orange-700 active:scale-[0.98] text-white rounded-xl py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-60" data-testid="consumo-modal-save">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <TrendingDown size={16} />}
              Registrar Consumo
            </button>
          </div>
        </form>
      </div>
    </div>
  , document.body);
}

// ─── TOOL MODAL ───────────────────────────────────────────
// ─── TOOL MODAL ───────────────────────────────────────────
function ToolModal({ open, onClose, onSaved, tool = null }) {
  const empty = { code: "", name: "", category: TOOL_CATEGORIES[0], value_pyg: "", notes: "" };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { if (open) { setForm(tool ? { ...tool, value_pyg: tool.value_pyg ?? "" } : empty); setError(""); } }, [open, tool]);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.code || !form.name) { setError("Complete los campos obligatorios"); return; }
    setSaving(true); setError("");
    try {
      const payload = { ...form, value_pyg: Number(form.value_pyg) || 0 };
      if (tool) await axios.put(`${API}/inventory/tools/${tool.id}`, payload, { withCredentials: true });
      else await axios.post(`${API}/inventory/tools`, payload, { withCredentials: true });
      toast.success(tool ? "Herramienta actualizada" : "Herramienta registrada");
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="tool-modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" data-testid="tool-modal">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-base font-bold text-gray-900">{tool ? "Editar Herramienta" : "Nueva Herramienta"}</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors" data-testid="tool-modal-close"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Código *</label><input name="code" value={form.code} onChange={handleChange} placeholder="TOOL-007" className={inputCls} data-testid="tool-code-input" /></div>
            <div><label className={labelCls}>Valor (₲)</label><input name="value_pyg" type="number" value={form.value_pyg} onChange={handleChange} placeholder="0" className={inputCls} data-testid="tool-value-input" /></div>
          </div>
          <div><label className={labelCls}>Nombre *</label><input name="name" value={form.name} onChange={handleChange} placeholder="Ej: Taladro Bosch 18V" className={inputCls} data-testid="tool-name-input" /></div>
          <div><label className={labelCls}>Categoría</label>
            <select name="category" value={form.category} onChange={handleChange} className={inputCls} data-testid="tool-category-select">
              {TOOL_CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div><label className={labelCls}>Notas</label><textarea name="notes" value={form.notes} onChange={handleChange} rows={2} placeholder="Observaciones, accesorios..." className={`${inputCls} resize-none`} data-testid="tool-notes-input" /></div>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2 text-sm" data-testid="tool-modal-error">{error}</div>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-3 text-sm font-medium hover:bg-gray-50 transition-all" data-testid="tool-modal-cancel">Cancelar</button>
            <button type="submit" disabled={saving} className="flex-1 bg-[#2E6DA4] hover:bg-[#245A8A] active:scale-[0.98] text-white rounded-xl py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-60" data-testid="tool-modal-save">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
              {tool ? "Guardar" : "Registrar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  , document.body);
}

// ─── ASSIGN MODAL ─────────────────────────────────────────
function AssignModal({ open, onClose, onSaved, tool }) {
  const [form, setForm] = useState({ assignee_name: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { if (open) { setForm({ assignee_name: "", notes: "" }); setError(""); } }, [open]);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.assignee_name.trim()) { setError("Ingrese el nombre del Residente"); return; }
    setSaving(true); setError("");
    try {
      await axios.post(`${API}/inventory/tools/${tool.id}/assign`, form, { withCredentials: true });
      toast.success(`Herramienta asignada a ${form.assignee_name}`);
      onSaved();
    } catch (err) { setError(err.response?.data?.detail || "Error al asignar"); }
    finally { setSaving(false); }
  };

  if (!open || !tool) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="assign-modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm" data-testid="assign-modal">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div><h2 className="text-base font-bold text-gray-900">Asignar Herramienta</h2><p className="text-xs text-gray-400 mt-0.5">{tool.code} — {tool.name}</p></div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors" data-testid="assign-modal-close"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div><label className={labelCls}>Residente / Operario *</label><input name="assignee_name" value={form.assignee_name} onChange={e => setForm(f => ({ ...f, assignee_name: e.target.value }))} placeholder="Nombre completo" className={`${inputCls} text-base`} data-testid="assign-name-input" /></div>
          <div><label className={labelCls}>Notas</label><input name="notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Para qué obra / proyecto..." className={inputCls} data-testid="assign-notes-input" /></div>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2 text-sm">{error}</div>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-3 font-medium hover:bg-gray-50 transition-all" data-testid="assign-modal-cancel">Cancelar</button>
            <button type="submit" disabled={saving} className="flex-1 bg-[#2E6DA4] hover:bg-[#245A8A] active:scale-[0.98] text-white rounded-xl py-3 font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-60" data-testid="assign-modal-save">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <User size={16} />}
              Asignar
            </button>
          </div>
        </form>
      </div>
    </div>
  , document.body);
}

// ─── RETURN MODAL ─────────────────────────────────────────
function ReturnModal({ open, onClose, onSaved, tool }) {
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setNotes(""); }, [open]);

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/inventory/tools/${tool.id}/return`, { notes }, { withCredentials: true });
      toast.success("Herramienta devuelta al inventario");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  if (!open || !tool) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="return-modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm" data-testid="return-modal">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div><h2 className="text-base font-bold text-gray-900">Confirmar Devolución</h2><p className="text-xs text-gray-400 mt-0.5">{tool.code} — {tool.name}</p></div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors" data-testid="return-modal-close"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm text-blue-700">
            Devolviendo de: <span className="font-semibold">{tool.current_assignee_name || "Asignado"}</span>
          </div>
          <div><label className={labelCls}>Observaciones (opcional)</label><textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Estado de la herramienta al devolver..." className={`${inputCls} resize-none`} data-testid="return-notes-input" /></div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-3 font-medium hover:bg-gray-50 transition-all" data-testid="return-modal-cancel">Cancelar</button>
            <button type="submit" disabled={saving} className="flex-1 bg-emerald-600 hover:bg-emerald-700 active:scale-[0.98] text-white rounded-xl py-3 font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-60" data-testid="return-modal-save">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
              Confirmar Devolución
            </button>
          </div>
        </form>
      </div>
    </div>
  , document.body);
}

// ─── REPORT MODAL ─────────────────────────────────────────
function ReportModal({ open, onClose, onSaved, tool }) {
  const [form, setForm] = useState({ report_type: "daño", notes: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { if (open) { setForm({ report_type: "daño", notes: "" }); setError(""); } }, [open]);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.notes.trim()) { setError("Describa el problema"); return; }
    setSaving(true); setError("");
    try {
      await axios.post(`${API}/inventory/tools/${tool.id}/report`, form, { withCredentials: true });
      toast.success(`Reporte de ${form.report_type} registrado`);
      onSaved();
    } catch (err) { setError(err.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  if (!open || !tool) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="report-modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm" data-testid="report-modal">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3"><div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center"><ShieldAlert size={16} className="text-red-600" /></div><div><h2 className="text-base font-bold text-gray-900">Reportar Problema</h2><p className="text-xs text-gray-400">{tool.code} — {tool.name}</p></div></div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors" data-testid="report-modal-close"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className={labelCls}>Tipo de Reporte</label>
            <div className="grid grid-cols-2 gap-2">
              {[{ val: "daño", label: "Daño", cls: "border-amber-400 bg-amber-50 text-amber-700" }, { val: "perdida", label: "Pérdida", cls: "border-red-400 bg-red-50 text-red-700" }].map(opt => (
                <button key={opt.val} type="button" onClick={() => setForm(f => ({ ...f, report_type: opt.val }))}
                  className={`py-3 rounded-xl border-2 font-semibold text-sm transition-all ${form.report_type === opt.val ? opt.cls : "border-gray-200 text-gray-500 hover:border-gray-300"}`}
                  data-testid={`report-type-${opt.val}`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div><label className={labelCls}>Descripción *</label><textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={3} placeholder="Describa el daño o pérdida en detalle..." className={`${inputCls} resize-none text-base`} data-testid="report-notes-input" /></div>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2 text-sm">{error}</div>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-3 font-medium hover:bg-gray-50 transition-all" data-testid="report-modal-cancel">Cancelar</button>
            <button type="submit" disabled={saving} className="flex-1 bg-red-600 hover:bg-red-700 active:scale-[0.98] text-white rounded-xl py-3 font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-60" data-testid="report-modal-save">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <ShieldAlert size={16} />}
              Enviar Reporte
            </button>
          </div>
        </form>
      </div>
    </div>
  , document.body);
}

// ─── MATERIALES TAB ───────────────────────────────────────
// Fix IQ (2026-10-31): "Se genera el «Pedido de Movimiento» desde «Compras» a «Inventario de Materiales» visualizando la
// reserva provisoria de la cantidad en el detalle de inventario. Aprobado el movimiento [...] reservando formalmente dicha
// cantidad" (CEN). Solo se muestra lo pedido en un Pedido de Movimiento todavía sin aprobar (estado «pendiente»): no cambia
// lo disponible ni la aprobación; la reserva formal sigue siendo la de siempre, al aprobar. El ítem se vincula al material
// como al aprobar: por código, después por id y por último por nombre (solo si un único material lo tiene). Herramientas no.
function reservasProvisorias(rsis, materials) {
  const porCodigo = {}, porId = {}, porNombre = {};
  (materials || []).forEach(m => {
    const c = String(m.code || "").trim(), n = String(m.name || "").trim();
    if (c) porCodigo[c] = m.id;
    porId[m.id] = m.id;
    if (n) porNombre[n] = n in porNombre ? null : m.id;
  });
  const out = {};
  (Array.isArray(rsis) ? rsis : []).forEach(r => {
    if (r.status !== "pendiente") return;
    (r.items || []).forEach(it => {
      if (it.origen === "stock_herramientas") return;
      const q = Number(it.quantity_pending ?? it.quantity) || 0;
      if (q <= 0) return;
      const c = String(it.origen_material_code || "").trim(), n = String(it.origen_material_name || "").trim();
      const id = (c && porCodigo[c]) || porId[it.origen_material_id || ""] || (n && porNombre[n]);
      if (!id) return;
      const o = out[id] || (out[id] = { cantidad: 0, pedidos: [] });
      o.cantidad = Math.round((o.cantidad + q) * 10000) / 10000;
      if (r.number && !o.pedidos.includes(r.number)) o.pedidos.push(r.number);
    });
  });
  return out;
}

function MaterialesTab({ materials, almacenes = [], loading, onNew, onImport, onImportExcel, onEdit, onRefresh }) {
  // Fix IQ (2026-10-31): reserva provisoria de los Pedidos de Movimiento sin aprobar (solo se muestra).
  const [provisorias, setProvisorias] = useState({});
  useEffect(() => {
    let vivo = true;
    axios.get(`${API}/purchases/stock-withdrawals`, { withCredentials: true })
      .then(({ data }) => { if (vivo) setProvisorias(reservasProvisorias(data, materials)); })
      .catch(() => { if (vivo) setProvisorias({}); });
    return () => { vivo = false; };
  }, [materials]);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("todos");
  const [expanded, setExpanded] = useState(null);
  const [deleting, setDeleting] = useState(null);
  // Fix BL (2026-09-08): "tener varios tipos de vista" (Edson, 06/09).
  const [vista, setVista] = useState("tarjetas");   // tarjetas | almacen | lista
  const [agruparTipo, setAgruparTipo] = useState(false);

  const categories = ["todos", ...MATERIAL_CATEGORIES.filter(c => materials.some(m => m.category === c))];

  // Fix DY (2026-09-23): "que ahí solo vayan apareciendo (…) el material buscado o
  // similar" (Edson, 23/09). La búsqueda ya no exige el texto exacto: ignora acentos
  // y mayúsculas y toma las palabras en cualquier orden, en nombre, código o marca
  // ("cable 2mm" encuentra "CABLE MULTIFILAR BLANCO DE 2mm2"). Todo lo que antes
  // aparecía sigue apareciendo.
  const filtered = materials.filter(m => {
    const matchSearch = !search.trim() || coincide(`${m.name || ""} ${m.code || ""} ${m.marca || ""}`, search);
    const matchCat = catFilter === "todos" || m.category === catFilter;
    return matchSearch && matchCat;
  });

  // Fix DY (2026-09-23): "Por almacén" arranca con los almacenes plegados y cada
  // encabezado despliega su lista; con un filtro puesto, los que coinciden se
  // muestran ya desplegados. "Poder filtrar por almacén y que solo aparezca el
  // almacén buscado" (Edson, 23/09).
  const [filtroAlmacen, setFiltroAlmacen] = useState("");
  const filtroActivo = !!search.trim() || catFilter !== "todos" || !!filtroAlmacen.trim();
  const ctxFiltro = `${search}|${catFilter}|${filtroAlmacen}`;
  const [plegado, setPlegado] = useState({ ctx: "", ids: {} });
  const cambiados = plegado.ctx === ctxFiltro ? plegado.ids : {};
  const almacenAbierto = (id) => (filtroActivo ? !cambiados[id] : !!cambiados[id]);
  const alternarAlmacen = (id) => setPlegado(p => {
    const base = p.ctx === ctxFiltro ? p.ids : {};
    return { ctx: ctxFiltro, ids: { ...base, [id]: !base[id] } };
  });

  // Fix BL (2026-09-08): el stock de cada almacén sale de las `locations` que ya
  // trae cada material; la lista de almacenes se usa para que también aparezcan
  // los que están en cero.
  const porAlmacen = (() => {
    const idx = {};
    (almacenes || []).forEach(a => { idx[a.id] = { id: a.id, nombre: a.nombre, tipo: a.tipo, lineas: [] }; });
    filtered.forEach(m => {
      (m.locations || []).forEach(l => {
        if (!(l.quantity > 0)) return;
        if (!idx[l.location_id]) idx[l.location_id] = { id: l.location_id, nombre: l.name || l.location_id, tipo: "otro", lineas: [] };
        idx[l.location_id].lineas.push({ m, cantidad: l.quantity, reservado: l.reserved || 0 });
      });
    });
    const orden = { central: 0, almacen_obra: 1, obra_deposito: 2, obrador: 3 };   // Fix IC: el Almacén de obra va junto al Central
    // Fix DY (2026-09-23): con un material buscado solo quedan los almacenes donde
    // está; con un almacén buscado, solo ese. Sin filtros, todos (también vacíos).
    const materialFiltrado = !!search.trim() || catFilter !== "todos";
    return Object.values(idx)
      .filter(a => a.tipo === "central" || !filtroAlmacen.trim() || coincide(a.nombre, filtroAlmacen))   // Fix II: el Central siempre primero
      .filter(a => !materialFiltrado || a.lineas.length > 0)
      .sort((a, b) =>
        (orden[a.tipo] ?? 9) - (orden[b.tipo] ?? 9) || (a.nombre || "").localeCompare(b.nombre || "", "es"));
  })();
  // Fix II (2026-10-30): «Por obra» — primero el Almacén Central (y lo que no es de una obra), y después las obras.
  const [senalObras, setSenalObras] = useState(null);
  // Fix IM (2026-10-30): "¿cuál sería la diferencia entre esos dos marcados?" (CEN, dos «FIRST MORRA»). Lo registrado con el
  // proyecto de una obra que ya tiene su Depósito en obra no se repite como un almacén suelto: se ve en el Depósito de esa obra.
  const [idsDeObras, setIdsDeObras] = useState(() => new Set());
  const almacenesSueltos = porAlmacen.filter(a => !["almacen_obra", "obra_deposito", "obrador"].includes(a.tipo) && !idsDeObras.has(a.id));
  const setTodosAlmacenes = (abrir) => {
    setPlegado({ ctx: ctxFiltro, ids: Object.fromEntries(porAlmacen.map(a => [a.id, filtroActivo ? !abrir : abrir])) });
    setSenalObras({ abrir, n: Date.now() });
  };

  // Fix BL (2026-09-08): la lista, con o sin encabezados por tipo de material.
  const filasLista = (() => {
    if (!agruparTipo) return filtered.map(m => ({ tipo: "material", m }));
    const porTipo = {};
    filtered.forEach(m => { (porTipo[m.category || "Sin tipo"] = porTipo[m.category || "Sin tipo"] || []).push(m); });
    const out = [];
    Object.keys(porTipo).sort((a, b) => a.localeCompare(b, "es")).forEach(k => {
      out.push({ tipo: "encabezado", clave: k.replace(/\s/g, "-"), label: k, cuenta: porTipo[k].length });
      porTipo[k].forEach(m => out.push({ tipo: "material", m }));
    });
    return out;
  })();

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este material? Esta acción no se puede deshacer.")) return;
    setDeleting(id);
    try {
      await axios.delete(`${API}/inventory/materials/${id}`, { withCredentials: true });
      toast.success("Material eliminado");
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setDeleting(null); }
  };

  if (loading) return <div className="space-y-3">{[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>;

  return (
    <div className="space-y-4" data-testid="materiales-tab">
      {/* Search + filters */}
      {/* Fix EW (2026-10-02): con la pantalla girada (o con muchos filtros) el buscador
          quedaba sin ancho y lo escrito no se leía. Ahora conserva un ancho mínimo y lo que no
          entra a su lado baja de renglón. */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
        <div className="relative flex-1 sm:min-w-[160px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por nombre o código..." className="w-full border border-gray-300 rounded-xl pl-9 pr-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4] bg-white" data-testid="materials-search" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {categories.map(c => (
            <button key={c} onClick={() => setCatFilter(c)} className={`px-3 py-2 rounded-xl text-xs font-semibold capitalize transition-all border whitespace-nowrap ${catFilter === c ? "bg-[#2E6DA4] text-white border-[#2E6DA4]" : "bg-white text-gray-600 border-gray-300 hover:border-[#2E6DA4]/40"}`} data-testid={`cat-filter-${c.replace(/\s/g, "-")}`}>
              {c === "todos" ? "Todos" : c}
            </button>
          ))}
        </div>
      </div>

      {/* Fix BL (2026-09-08): mismo patrón del switch de Herramientas (Fix AX). */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex rounded-xl border border-gray-300 overflow-hidden flex-shrink-0" data-testid="materiales-vista-switch">
          {[["tarjetas", "Tarjetas"], ["almacen", "Por obra"], ["lista", "Lista"]].map(([k, label]) => (
            <button key={k} onClick={() => setVista(k)}
              className={`px-3 py-2.5 text-xs font-semibold transition-all ${vista === k ? "bg-[#2E6DA4] text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
              data-testid={`materiales-vista-${k}`}>
              {label}
            </button>
          ))}
        </div>
        {vista === "lista" && (
          <button onClick={() => setAgruparTipo(v => !v)}
            className={`px-3 py-2.5 rounded-xl text-xs font-semibold border transition-all ${agruparTipo ? "bg-violet-600 text-white border-violet-600" : "bg-white text-gray-600 border-gray-300 hover:border-violet-300"}`}
            data-testid="materiales-agrupar-tipo">
            {agruparTipo ? "Agrupado por tipo" : "Agrupar por tipo"}
          </button>
        )}
        {vista === "almacen" && (
          <>
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={filtroAlmacen} onChange={e => setFiltroAlmacen(e.target.value)} placeholder="Filtrar obra o almacén..."
                className="border border-gray-300 rounded-xl pl-8 pr-3 py-2.5 text-xs bg-white w-48" data-testid="materiales-filtro-almacen" />
            </div>
            <button onClick={() => setTodosAlmacenes(true)} className="px-3 py-2.5 rounded-xl text-xs font-semibold border border-gray-300 bg-white text-gray-600 hover:border-[#2E6DA4]/40" data-testid="materiales-desplegar-todos">Desplegar todos</button>
            <button onClick={() => setTodosAlmacenes(false)} className="px-3 py-2.5 rounded-xl text-xs font-semibold border border-gray-300 bg-white text-gray-600 hover:border-[#2E6DA4]/40" data-testid="materiales-plegar-todos">Plegar todos</button>
          </>
        )}
        <span className="text-xs text-gray-400" data-testid="materiales-contador">
          {filtered.length} de {materials.length} materiales
        </span>
      </div>

      {/* Add button */}
      {/* Fix EW (2026-10-02): con el celular girado los tres botones no entraban y el último se salía. */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
        <button onClick={onNew} className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] active:scale-[0.98] text-white px-4 py-2.5 rounded-xl text-sm font-semibold transition-all shadow-sm" data-testid="new-material-btn">
          <Plus size={16} /><span>Nuevo Material</span>
        </button>
        <button onClick={onImport} className="flex-1 sm:flex-none flex items-center justify-center gap-2 border-2 border-dashed border-violet-300 text-violet-700 hover:bg-violet-50 active:scale-[0.98] px-4 py-2.5 rounded-xl text-sm font-semibold transition-all" data-testid="import-materials-btn">
          <FileUp size={16} /><span>Importar desde Excel/PDF (IA)</span>
        </button>
        <button onClick={onImportExcel} className="flex-1 sm:flex-none flex items-center justify-center gap-2 border-2 border-dashed border-emerald-300 text-emerald-700 hover:bg-emerald-50 active:scale-[0.98] px-4 py-2.5 rounded-xl text-sm font-semibold transition-all" data-testid="import-materials-excel-btn">
          <FileSpreadsheet size={16} /><span>Importar Excel (Plantilla)</span>
        </button>
      </div>

      {/* Fix BL (2026-09-08): "Agrupación por Almacen (…) que nos salga todos los
          almacenes" (Edson, 06/09). Cada almacén con lo que tiene adentro; los
          vacíos también, para que se vea que existen y están en cero. */}
      {vista === "almacen" && (
        <div className="space-y-3" data-testid="materiales-por-almacen">
          {almacenesSueltos.filter(a => a.tipo === "central").length === 0 && (
            <div className="bg-white rounded-xl border border-gray-200/60 p-8 text-center">
              <Warehouse size={32} className="text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">{filtroActivo ? "El Almacén Central no tiene materiales que coincidan" : "Todavía no hay Almacén Central cargado"}</p>
            </div>
          )}
          {[...almacenesSueltos.filter(a => a.tipo === "central"), { id: "__obras__" }, ...almacenesSueltos.filter(a => a.tipo !== "central")].map(a => a.id === "__obras__" ? (
            <PorObraVista key="__obras__" filtro={filtroAlmacen} porAlmacen={porAlmacen} senal={senalObras}
              onObras={(obs) => setIdsDeObras(new Set(obs.flatMap(o => o.proyectos || [])))} />
          ) : (
            <div key={a.id} className="bg-white rounded-xl border border-gray-200/60 overflow-hidden" data-testid={`almacen-card-${a.id}`}>
              {/* Fix DY (2026-09-23): el encabezado despliega o pliega la lista. */}
              <button type="button" onClick={() => alternarAlmacen(a.id)}
                className="w-full px-4 py-3 bg-[#EBF3FB] hover:bg-[#DDEBF8] flex items-center justify-between gap-3 text-left"
                data-testid={`almacen-toggle-${a.id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  {almacenAbierto(a.id) ? <ChevronDown size={15} className="text-[#1A4B7A] flex-shrink-0" /> : <ChevronRight size={15} className="text-[#1A4B7A] flex-shrink-0" />}
                  {a.tipo === "central" ? <Warehouse size={15} className="text-[#1A4B7A] flex-shrink-0" /> : <Building2 size={15} className="text-[#1A4B7A] flex-shrink-0" />}
                  <span className="text-sm font-bold text-[#1A4B7A] truncate">{a.nombre}</span>
                  {/* Fix IP (2026-10-30): "que los vaya detallando cuál es qué tipo es cada uno" (CEN). */}
                  {a.tipo !== "central" && (
                    <span className="text-[10px] font-semibold text-gray-600 bg-white border border-gray-200 rounded-full px-2 py-0.5 whitespace-nowrap flex-shrink-0" data-testid={`almacen-tipo-${a.id}`}>
                      {a.id === "obra_rapida" ? "Obras rápidas, todas juntas" : a.tipo === "excedentes" ? "Excedentes" : "Entregado antes a esta obra · sin Depósito abierto"}
                    </span>
                  )}
                </div>
                <span className="text-xs font-semibold text-[#1A4B7A] whitespace-nowrap">
                  {a.lineas.length} material{a.lineas.length !== 1 ? "es" : ""}
                </span>
              </button>
              {!almacenAbierto(a.id) ? null : a.lineas.length === 0 ? (
                <p className="px-4 py-3 text-xs text-gray-400 italic">Sin stock en este almacén.</p>
              ) : (
                <div className="divide-y divide-gray-50">
                  {a.lineas.map(l => (
                    <div key={l.m.id} className="px-4 py-2 flex items-center justify-between gap-3 text-sm">
                      <div className="min-w-0">
                        <span className="text-xs font-mono text-gray-400 mr-1.5">{l.m.code}</span>
                        <span className="text-gray-800">{l.m.name}</span>
                        {l.m.marca && <span className="text-xs text-gray-400"> · {l.m.marca}</span>}
                      </div>
                      <span className="font-semibold text-gray-900 tabular-nums whitespace-nowrap">
                        {l.cantidad} {l.m.unit}
                        {l.reservado > 0 && <span className="text-amber-600 font-normal ml-1">({l.reservado} reservado)</span>}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Fix BL (2026-09-08): "Lista del 100% del material en el stock actual, que
          disponga en otras columnas detalles de unidad de medida, cantidad y
          almacenes en las que se encuentran" (Edson, 06/09). */}
      {vista === "lista" && (
        <div className="bg-white rounded-xl border border-gray-200/60 overflow-x-auto" data-testid="materiales-lista">
          <table className="w-full text-xs min-w-[860px]">
            <thead className="bg-[#EBF3FB]">
              <tr className="text-left text-[#1A4B7A] uppercase tracking-wide">
                <th className="px-3 py-2 font-bold">Código</th>
                <th className="px-3 py-2 font-bold">Material</th>
                <th className="px-3 py-2 font-bold">Tipo</th>
                <th className="px-3 py-2 font-bold">Unidad</th>
                <th className="px-3 py-2 font-bold text-right">Cantidad</th>
                <th className="px-3 py-2 font-bold">Almacenes</th>
              </tr>
            </thead>
            <tbody>
              {filasLista.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-400">No se encontraron materiales</td></tr>
              )}
              {filasLista.map(f => f.tipo === "encabezado" ? (
                <tr key={f.clave} className="bg-gray-50" data-testid={`lista-grupo-${f.clave}`}>
                  <td colSpan={6} className="px-3 py-1.5 font-bold text-gray-600">{f.label} <span className="font-normal text-gray-400">({f.cuenta})</span></td>
                </tr>
              ) : (
                <tr key={f.m.id} className="border-t border-gray-50" data-testid={`lista-material-${f.m.id}`}>
                  <td className="px-3 py-2 font-mono text-gray-400 whitespace-nowrap">{f.m.code}</td>
                  <td className="px-3 py-2 text-gray-800">{f.m.name}{f.m.marca && <span className="text-gray-400"> · {f.m.marca}</span>}</td>
                  <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{f.m.category}</td>
                  <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{f.m.unit}</td>
                  <td className={`px-3 py-2 text-right font-semibold tabular-nums whitespace-nowrap ${f.m.is_low_stock ? "text-red-600" : "text-gray-900"}`}>
                    {f.m.total_stock}
                  </td>
                  <td className="px-3 py-2 text-gray-600">
                    {(f.m.locations || []).filter(l => (l.quantity || 0) > 0).length === 0 ? (
                      <span className="text-gray-300">sin stock</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {(f.m.locations || []).filter(l => (l.quantity || 0) > 0).map(l => (
                          <span key={l.location_id} className="inline-flex px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 whitespace-nowrap">
                            {l.name}: <span className="font-semibold ml-1">{l.quantity}</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Material list */}
      {vista === "tarjetas" && (
      <div className="space-y-2" data-testid="materials-list">
        {filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200/60 p-8 text-center">
            <Package size={32} className="text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">No se encontraron materiales</p>
          </div>
        ) : filtered.map(m => {
          // Fix GF (2026-10-15): el número principal es lo disponible en el Almacén Central (físico menos
          // reservado); lo que hay en las demás ubicaciones se resume debajo y se abre con un clic.
          const dispCentral = disponibleCentral(m);
          const fisicoCentral = Number((m.locations || []).find(l => l.location_id === "almacen_central")?.quantity) || 0;
          const otrasUbic = (m.locations || []).filter(l => l.location_id !== "almacen_central" && (l.quantity || 0) > 0);
          const otrasCant = Math.round(otrasUbic.reduce((s, l) => s + (Number(l.quantity) || 0), 0) * 10000) / 10000;
          const pct = stockPct(dispCentral, m.min_stock);
          const clr = stockColor(pct);
          const isExpanded = expanded === m.id;
          return (
            <div key={m.id} className={`bg-white rounded-xl border transition-all duration-200 hover:shadow-md ${m.is_low_stock ? "border-red-200" : "border-gray-200/60"}`} data-testid="material-row">
              <div className="p-4 flex items-center gap-3 cursor-pointer" onClick={() => setExpanded(isExpanded ? null : m.id)} data-testid={`material-expand-${m.id}`}>
                {/* Status dot */}
                <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${clr.bar}`} />
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-mono font-semibold text-gray-400">{m.code}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${m.is_low_stock ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-600"}`}>{m.category}</span>
                  </div>
                  <p className="text-sm font-semibold text-gray-900 mt-0.5 truncate">{m.name}{m.marca && <span className="text-xs font-normal text-gray-400 ml-1.5" data-testid={`material-marca-${m.id}`}>· {m.marca}</span>}</p>
                  <div className="flex items-center gap-3 mt-1.5">
                    <div className="flex-1 bg-gray-100 rounded-full h-1.5 max-w-[120px]">
                      <div className={`h-1.5 rounded-full transition-all duration-500 ${clr.bar}`} style={{ width: `${Math.min(100, pct)}%` }} />
                    </div>
                    <span className={`text-xs font-semibold ${clr.text}`} data-testid={`material-disponible-${m.id}`}>{dispCentral} / {m.min_stock} {m.unit}</span>
                    <span className="text-[10px] text-gray-400">disponible en Almacén Central</span>
                  </div>
                  {m.total_reserved > 0 && (
                    <p className="text-[10px] text-amber-600 mt-1" data-testid={`material-reserved-${m.id}`}>
                      Reservado: <span className="font-semibold">{m.total_reserved} {m.unit}</span> · Físico en Almacén Central: <span className="font-semibold">{fisicoCentral} {m.unit}</span>
                    </p>
                  )}
                  {provisorias[m.id] && (
                    <p className="text-[10px] text-violet-700 mt-1" data-testid={`material-reserva-provisoria-${m.id}`}>
                      Reserva provisoria: <span className="font-semibold">{provisorias[m.id].cantidad} {m.unit}</span> · Pedido de Movimiento sin aprobar ({provisorias[m.id].pedidos.join(", ")}); se reserva al aprobar
                    </p>
                  )}
                  {otrasCant > 0 && (
                    <button type="button" onClick={e => { e.stopPropagation(); setExpanded(isExpanded ? null : m.id); }}
                      className="mt-1 inline-flex items-center gap-1 text-[11px] text-sky-700 hover:text-sky-900 hover:underline" data-testid={`material-otros-${m.id}`}>
                      <Building2 size={11} /> En otros almacenes y obras: <span className="font-semibold">{otrasCant} {m.unit}</span> · {otrasUbic.length} {otrasUbic.length === 1 ? "ubicación" : "ubicaciones"} — ver detalle
                    </button>
                  )}
                </div>
                {/* Actions */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={e => { e.stopPropagation(); onEdit(m); }} className="p-1.5 text-gray-400 hover:text-[#2E6DA4] hover:bg-[#EBF2F9] rounded-lg transition-all text-xs" data-testid={`edit-material-${m.id}`} title="Editar">
                    <FileText size={15} />
                  </button>
                  <button onClick={e => { e.stopPropagation(); handleDelete(m.id); }} disabled={deleting === m.id} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all" data-testid={`delete-material-${m.id}`} title="Eliminar">
                    {deleting === m.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  </button>
                  {isExpanded ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
                </div>
              </div>
              {/* Expanded: locations */}
              {isExpanded && (
                <div className="border-t border-gray-100 px-4 py-3 bg-gray-50/50 rounded-b-xl" data-testid={`material-locations-${m.id}`}>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Stock por Ubicación</p>
                  <div className="space-y-1.5">
                    {ubicacionesCentralPrimero(m.locations).map(loc => (
                      <div key={loc.location_id} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2 text-gray-600">
                          {loc.location_id === "almacen_central" ? <Warehouse size={13} className="text-gray-400" /> : <Building2 size={13} className="text-gray-400" />}
                          <span>{loc.name}</span>
                        </div>
                        <span className="font-semibold text-gray-900 tabular-nums">
                          {loc.quantity} {m.unit}
                          {loc.reserved > 0 && <span className="text-amber-600 font-normal ml-1">({loc.reserved} reservado)</span>}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between text-xs mt-2 pt-2 border-t border-gray-100" data-testid={`material-summary-${m.id}`}>
                    <span className="text-gray-400">Físico total: <span className="font-semibold text-gray-700">{m.total_stock} {m.unit}</span></span>
                    <span className="text-amber-600">Reservado: <span className="font-semibold">{m.total_reserved} {m.unit}</span></span>
                    <span className="text-emerald-600">Disponible en Almacén Central: <span className="font-semibold">{dispCentral} {m.unit}</span></span>
                  </div>
                  {provisorias[m.id] && (
                    <p className="text-xs text-violet-700 mt-2 pt-2 border-t border-gray-100" data-testid={`material-detalle-provisoria-${m.id}`}>
                      Reserva provisoria: <span className="font-semibold">{provisorias[m.id].cantidad} {m.unit}</span> en Pedido de Movimiento sin aprobar: {provisorias[m.id].pedidos.join(", ")}. No descuenta lo disponible: se reserva al aprobar.
                    </p>
                  )}
                  {m.unit_cost_pyg > 0 && <p className="text-xs text-gray-400 mt-2 pt-2 border-t border-gray-100">Costo unit.: {formatPYGCompact(m.unit_cost_pyg)} — Valor total: {formatPYGCompact(m.total_stock * m.unit_cost_pyg)}</p>}
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}

// ─── MOVIMIENTOS TAB ──────────────────────────────────────
function MovimientosTab({ movements, materials, projects, loading, onNewEntry, onNewTransfer, onNewEntryOC, onNewConsumo, onRefresh }) {
  const [typeFilter, setTypeFilter] = useState("todos");
  const [materialFilter, setMaterialFilter] = useState("todos");
  // Fix EI (2026-09-27): corregir la cantidad de una entrada o un consumo, con acta.
  const { user } = useAuth();
  const puedeEditar = roleAllowed(user?.role, MOV_EDIT_ROLES);
  const [editMov, setEditMov] = useState(null);

  const filtered = movements.filter(m => {
    const matchType = typeFilter === "todos" || m.type === typeFilter;
    const matchMat = materialFilter === "todos" || m.material_id === materialFilter;
    return matchType && matchMat;
  });

  if (loading) return <div className="space-y-3">{[1, 2, 3].map(i => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>;

  return (
    <div className="space-y-4" data-testid="movimientos-tab">
      {/* Primary actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <button onClick={onNewEntry} className="flex items-center justify-center gap-3 bg-emerald-600 hover:bg-emerald-700 active:scale-[0.98] text-white rounded-xl py-4 font-semibold transition-all shadow-sm" data-testid="new-entry-btn">
          <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center"><ArrowDownToLine size={18} /></div>
          <div className="text-left"><p className="font-semibold">Registrar Entrada</p><p className="text-xs text-emerald-100 font-normal">Manual, sin OC</p></div>
        </button>
        <button onClick={onNewEntryOC} className="flex items-center justify-center gap-3 bg-teal-600 hover:bg-teal-700 active:scale-[0.98] text-white rounded-xl py-4 font-semibold transition-all shadow-sm" data-testid="new-entry-oc-btn">
          <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center"><FileText size={18} /></div>
          <div className="text-left"><p className="font-semibold">Entrada desde OC</p><p className="text-xs text-teal-100 font-normal">Precarga ítems y costos</p></div>
        </button>
        <button onClick={onNewTransfer} className="flex items-center justify-center gap-3 bg-[#2E6DA4] hover:bg-[#245A8A] active:scale-[0.98] text-white rounded-xl py-4 font-semibold transition-all shadow-sm" data-testid="new-transfer-btn">
          <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center"><ArrowRightLeft size={18} /></div>
          <div className="text-left"><p className="font-semibold">Transferir</p><p className="text-xs text-blue-100 font-normal">Entre almacenes/obras</p></div>
        </button>
        <button onClick={onNewConsumo} className="flex items-center justify-center gap-3 bg-orange-600 hover:bg-orange-700 active:scale-[0.98] text-white rounded-xl py-4 font-semibold transition-all shadow-sm" data-testid="new-consumo-btn">
          <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center"><TrendingDown size={18} /></div>
          <div className="text-left"><p className="font-semibold">Registrar Consumo</p><p className="text-xs text-orange-100 font-normal">Instalado en obra</p></div>
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex gap-2 flex-wrap">
          {["todos", "entry", "transfer", "consumo"].map(t => (
            <button key={t} onClick={() => setTypeFilter(t)} className={`px-3 py-2 rounded-xl text-xs font-semibold transition-all border ${typeFilter === t ? "bg-[#2E6DA4] text-white border-[#2E6DA4]" : "bg-white text-gray-600 border-gray-300 hover:border-[#2E6DA4]/40"}`} data-testid={`mov-filter-${t}`}>
              {t === "todos" ? "Todos" : t === "entry" ? "Entradas" : t === "transfer" ? "Transferencias" : "Consumo"}
            </button>
          ))}
        </div>
        <SelectBuscable value={materialFilter === "todos" ? "" : materialFilter} onChange={v => setMaterialFilter(v || "todos")}
          options={opcionesMaterial(materials)} vacio="Todos los materiales" placeholderBuscar="Buscar código o material..."
          className="border border-gray-300 rounded-xl px-3 py-2 text-sm bg-white min-w-[260px]" testId="mov-material-filter" />
      </div>

      {/* Movement history */}
      <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm overflow-hidden" data-testid="movements-table">
        {filtered.length === 0 ? (
          <div className="p-8 text-center"><ArrowRightLeft size={32} className="text-gray-300 mx-auto mb-2" /><p className="text-gray-500 text-sm">No hay movimientos registrados</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-gray-100 bg-gray-50/80"><th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Tipo</th><th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Material</th><th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Cant.</th><th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden sm:table-cell">Origen → Destino</th><th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">OC / Proveedor</th><th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Fecha</th><th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden lg:table-cell">Usuario</th><th className="px-4 py-3"></th></tr></thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(mv => {
                  const typeInfo = MOV_TYPE[mv.type] || MOV_TYPE.adjustment;
                  const TypeIcon = typeInfo.icon;
                  return (
                    <tr key={mv.id} className="hover:bg-gray-50/60 transition-colors" data-testid="movement-row">
                      <td className="px-4 py-3"><span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${typeInfo.cls}`}><TypeIcon size={11} />{typeInfo.label}</span></td>
                      <td className="px-4 py-3"><p className="font-medium text-gray-900 text-xs">{mv.material_name}</p><p className="text-xs text-gray-400 font-mono">{mv.material_code}</p></td>
                      <td className="px-4 py-3 font-semibold tabular-nums">
                        {mv.quantity} <span className="text-gray-400 font-normal">{mv.unit}</span>
                        {mv.editado && <span className="block text-[10px] font-semibold text-amber-600" data-testid={`mov-editado-${mv.id}`}>Corregida (antes {mv.cantidad_original})</span>}
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <span>{mv.from_location_name || "—"}</span>
                          {mv.to_location_name && <><ArrowRight size={10} className="text-gray-300 flex-shrink-0" /><span className="text-gray-700 font-medium">{mv.to_location_name}</span></>}
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell text-xs text-gray-500">{mv.po_number ? <span className="font-mono">{mv.po_number}</span> : mv.supplier || "—"}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{formatDate(mv.created_at)}</td>
                      <td className="px-4 py-3 hidden lg:table-cell text-xs text-gray-500">{mv.user_name}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {mv.editado && (
                          <button onClick={() => descargarActaMovimiento(mv)} className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 border border-amber-200 hover:bg-amber-50 rounded-lg px-2 py-1 mr-1" data-testid={`mov-acta-${mv.id}`}>
                            <FileText size={11} /> Acta
                          </button>
                        )}
                        {puedeEditar && (mv.type === "entry" || mv.type === "consumo") && (
                          <button onClick={() => setEditMov(mv)} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#2E6DA4] border border-[#2E6DA4]/30 hover:bg-[#EBF2F9] rounded-lg px-2 py-1" data-testid={`mov-editar-${mv.id}`}>
                            <Pencil size={11} /> Editar cant.
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <EditarMovimientoModal mov={editMov} onClose={() => setEditMov(null)} onSaved={() => { setEditMov(null); onRefresh(); }} />
    </div>
  );
}

// Fix EI (2026-09-27): acta de modificación de un movimiento (motivo + imagen de la remisión).
async function descargarActaMovimiento(mv) {
  try {
    const res = await axios.get(`${API}/inventory/movements/${mv.id}/acta-pdf`, { withCredentials: true, responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Acta_${mv.material_code || mv.id}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    toast.error("No se pudo generar el acta");
  }
}

function EditarMovimientoModal({ mov, onClose, onSaved }) {
  const [cantidad, setCantidad] = useState("");
  const [motivo, setMotivo] = useState("");
  const [remision, setRemision] = useState(null);   // { base64, nombre }
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (mov) { setCantidad(String(mov.quantity ?? "")); setMotivo(""); setRemision(null); } }, [mov]);
  if (!mov) return null;

  const leerImagen = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\/(png|jpe?g)$/i.test(f.type)) { toast.error("La remisión tiene que ser una imagen JPG o PNG"); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("La imagen supera los 5 MB"); return; }
    const r = new FileReader();
    r.onload = () => setRemision({ base64: r.result, nombre: f.name });
    r.readAsDataURL(f);
  };
  const guardar = async () => {
    const n = Number(cantidad);
    if (!(n > 0)) { toast.error("Ingresá la cantidad real"); return; }
    if (n === Number(mov.quantity)) { toast.error("La cantidad es la misma que la registrada"); return; }
    if (!motivo.trim()) { toast.error("Escribí el motivo de la modificación"); return; }
    if (!remision) { toast.error("Adjuntá la imagen de la remisión"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/inventory/movements/${mov.id}/cantidad`,
        { cantidad: n, motivo: motivo.trim(), remision_base64: remision.base64, remision_nombre: remision.nombre },
        { withCredentials: true });
      toast.success("Cantidad corregida · el acta quedó registrada");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo corregir la cantidad"); }
    finally { setSaving(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="mov-editar-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[92vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-gray-900">Corregir cantidad</h2>
            <p className="text-xs text-gray-400">{mov.type === "entry" ? "Entrada" : "Consumo"} · {mov.material_code} — {mov.material_name}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="bg-gray-50 rounded-xl px-3 py-2 text-xs text-gray-600">
            Registrada: <strong>{mov.quantity} {mov.unit}</strong>{mov.po_number ? ` · OC ${mov.po_number}` : ""}
          </div>
          <div><label className={labelCls}>Cantidad real *</label>
            <input type="number" min="0" value={cantidad} onChange={e => setCantidad(e.target.value)} className={inputCls} data-testid="mov-editar-cantidad" />
          </div>
          <div><label className={labelCls}>Motivo *</label>
            <textarea value={motivo} onChange={e => setMotivo(e.target.value)} rows={2} placeholder="Ej: se tipeó mal / el proveedor entregó otra cantidad"
              className={`${inputCls} resize-none`} data-testid="mov-editar-motivo" />
          </div>
          <div><label className={labelCls}>Imagen de la remisión *</label>
            <input type="file" accept="image/png,image/jpeg" onChange={leerImagen} className="block w-full text-xs" data-testid="mov-editar-remision" />
            {remision && <p className="text-[11px] text-emerald-700 mt-1">{remision.nombre}</p>}
          </div>
          <p className="text-[11px] text-gray-400">Se ajusta el stock del almacén por la diferencia y queda un acta con el motivo y la remisión.</p>
          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2.5 text-sm font-medium hover:bg-gray-50">Cancelar</button>
            <button onClick={guardar} disabled={saving} className="flex-1 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="mov-editar-guardar">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Guardar y generar acta
            </button>
          </div>
        </div>
      </div>
    </div>, document.body);
}

// ─── HERRAMIENTAS: TRASLADO DE UBICACIÓN + PDF (CEN 2.0 FASE 5 CHECKPOINT A) ─
// Fix P (2026-08-31): el PDF de cambio de responsable ahora se genera en el
// servidor (reportlab, encabezado desde Configuración) en vez de armarse en
// el navegador con jsPDF.
async function downloadTrasladoPdf(toolId, eventoId, code) {
  try {
    const res = await axios.get(`${API}/inventory/tools/${toolId}/traslado-pdf/${eventoId}`, {
      withCredentials: true, responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Traslado_${code}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    toast.error("Error al generar el PDF de traslado");
  }
}

function ToolTransferLocationModal({ open, onClose, onSaved, tool, almacenes }) {
  const [almacenId, setAlmacenId] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { if (open) { setAlmacenId(""); setNotes(""); setError(""); } }, [open]);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!almacenId) { setError("Seleccione el almacén/obra destino"); return; }
    setSaving(true); setError("");
    try {
      const alm = almacenes.find(a => a.id === almacenId);
      const { data } = await axios.put(`${API}/inventory/tools/${tool.id}/transfer-location`, {
        almacen_id: almacenId, almacen_nombre: alm?.nombre || "", notes,
      }, { withCredentials: true });
      await downloadTrasladoPdf(tool.id, data.evento_id, tool.code);
      toast.success("Herramienta trasladada — PDF de cambio de responsable generado");
      onSaved();
    } catch (err) { setError(err.response?.data?.detail || "Error al trasladar"); }
    finally { setSaving(false); }
  };

  if (!open || !tool) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="tool-transfer-modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm" data-testid="tool-transfer-modal">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div><h2 className="text-base font-bold text-gray-900">Trasladar Herramienta</h2><p className="text-xs text-gray-400 mt-0.5">{tool.code} — {tool.name}</p></div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors" data-testid="tool-transfer-modal-close"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="bg-gray-50 rounded-xl px-3 py-2 text-xs text-gray-600">Ubicación actual: <span className="font-semibold">{tool.current_almacen_nombre || "Almacén Central"}</span></div>
          <div><label className={labelCls}>Nueva Ubicación *</label>
            <select value={almacenId} onChange={e => setAlmacenId(e.target.value)} className={inputCls} data-testid="tool-transfer-almacen-select">
              <option value="">Seleccionar destino...</option>
              {almacenes.filter(a => a.id !== tool.current_almacen_id).map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
            </select>
          </div>
          <div><label className={labelCls}>Notas</label><input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Motivo del traslado..." className={inputCls} data-testid="tool-transfer-notes-input" /></div>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2 text-sm">{error}</div>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-3 font-medium hover:bg-gray-50 transition-all" data-testid="tool-transfer-modal-cancel">Cancelar</button>
            <button type="submit" disabled={saving} className="flex-1 bg-[#2E6DA4] hover:bg-[#245A8A] active:scale-[0.98] text-white rounded-xl py-3 font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-60" data-testid="tool-transfer-modal-save">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <ArrowRightLeft size={16} />}
              Trasladar + Generar PDF
            </button>
          </div>
        </form>
      </div>
    </div>
  , document.body);
}

// ─── HERRAMIENTAS: HOJA DE MANTENIMIENTO — CEN 2.0 FASE 5 CHECKPOINT A ──────
function ToolMaintenanceModal({ open, onClose, tool }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ fecha: "", tipo: "Preventivo", descripcion: "", costo: "", proximo_mantenimiento: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    if (!tool) return;
    setLoading(true);
    axios.get(`${API}/inventory/tools/${tool.id}/maintenance`, { withCredentials: true })
      .then(({ data }) => setItems(data)).catch(() => toast.error("Error cargando mantenimiento"))
      .finally(() => setLoading(false));
  }, [tool]);

  useEffect(() => {
    if (open) { setForm({ fecha: new Date().toISOString().slice(0, 10), tipo: "Preventivo", descripcion: "", costo: "", proximo_mantenimiento: "" }); setError(""); load(); }
  }, [open, load]);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.fecha || !form.descripcion.trim()) { setError("Complete fecha y descripción"); return; }
    setSaving(true); setError("");
    try {
      await axios.post(`${API}/inventory/tools/${tool.id}/maintenance`, {
        ...form, costo: Number(form.costo) || 0, proximo_mantenimiento: form.proximo_mantenimiento || null,
      }, { withCredentials: true });
      toast.success("Mantenimiento registrado");
      setForm(f => ({ ...f, descripcion: "", costo: "" }));
      load();
    } catch (err) { setError(err.response?.data?.detail || "Error al registrar"); }
    finally { setSaving(false); }
  };

  if (!open || !tool) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="tool-maintenance-modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto" data-testid="tool-maintenance-modal">
        <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between rounded-t-2xl">
          <div><h2 className="text-base font-bold text-gray-900">Hoja de Mantenimiento</h2><p className="text-xs text-gray-400 mt-0.5">{tool.code} — {tool.name}</p></div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors" data-testid="tool-maintenance-modal-close"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-3 border-b border-gray-100">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Fecha *</label><input type="date" value={form.fecha} onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))} className={inputCls} data-testid="tool-maintenance-fecha-input" /></div>
            <div><label className={labelCls}>Tipo</label>
              <select value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))} className={inputCls} data-testid="tool-maintenance-tipo-select">
                <option>Preventivo</option><option>Correctivo</option><option>Calibración</option>
              </select>
            </div>
          </div>
          <div><label className={labelCls}>Descripción *</label><input value={form.descripcion} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} placeholder="Ej: Cambio de rodamientos" className={inputCls} data-testid="tool-maintenance-desc-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Costo (₲)</label><input type="number" value={form.costo} onChange={e => setForm(f => ({ ...f, costo: e.target.value }))} className={inputCls} data-testid="tool-maintenance-costo-input" /></div>
            <div><label className={labelCls}>Próximo mantenimiento</label><input type="date" value={form.proximo_mantenimiento} onChange={e => setForm(f => ({ ...f, proximo_mantenimiento: e.target.value }))} className={inputCls} data-testid="tool-maintenance-proximo-input" /></div>
          </div>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2 text-sm">{error}</div>}
          <button type="submit" disabled={saving} className="w-full bg-[#2E6DA4] hover:bg-[#245A8A] active:scale-[0.98] text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-60" data-testid="tool-maintenance-save-btn">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Registrar
          </button>
        </form>
        <div className="p-6 space-y-2" data-testid="tool-maintenance-history">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Historial</p>
          {loading ? <Skeleton className="h-16 rounded-xl" /> : items.length === 0 ? (
            <p className="text-xs text-gray-400 italic py-3 text-center">Sin registros de mantenimiento todavía.</p>
          ) : items.map(it => (
            <div key={it.id} className="border border-gray-100 rounded-xl px-3 py-2 flex items-center justify-between text-xs" data-testid={`tool-maintenance-row-${it.id}`}>
              <div><p className="font-semibold text-gray-800">{it.tipo} — {formatDate(it.fecha)}</p><p className="text-gray-500">{it.descripcion}</p>{it.proximo_mantenimiento && <p className="text-amber-600">Próximo: {formatDate(it.proximo_mantenimiento)}</p>}</div>
              {it.costo > 0 && <span className="font-semibold text-gray-700">{formatPYGCompact(it.costo)}</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  , document.body);
}

// ─── HERRAMIENTAS TAB ─────────────────────────────────────
// ─── HERRAMIENTAS TAB ─────────────────────────────────────
function HerramientasTab({ tools, loading, onNew, onImportExcel, onAssign, onReturn, onReport, onTransfer, onMaintenance, onRefresh, onEdit }) {
  const [statusFilter, setStatusFilter] = useState("todos");
  const [search, setSearch] = useState("");
  // Fix AX (2026-09-06): "lo ideal creo sería tener encabezados de obras,
  // entonces agrupar esos cuadros en columnas de obras" (Edson, 05/09). Con
  // muchas herramientas la grilla continua no deja ver qué hay en cada obra.
  const [vista, setVista] = useState("obra");   // "obra" | "lista"

  const filtered = tools.filter(t => {
    const matchStatus = statusFilter === "todos" || t.status === statusFilter;
    const matchSearch = !search || t.name.toLowerCase().includes(search.toLowerCase()) || t.code.toLowerCase().includes(search.toLowerCase());
    return matchStatus && matchSearch;
  });

  if (loading) return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} className="h-40 rounded-xl" />)}</div>;

  return (
    <div className="space-y-4" data-testid="herramientas-tab">
      {/* Controls */}
      {/* Fix EW (2026-10-02): el buscador conserva un ancho mínimo; lo que no entra baja de renglón. */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 items-start sm:items-center">
        <div className="relative flex-1 sm:min-w-[160px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar herramienta..." className="w-full border border-gray-300 rounded-xl pl-9 pr-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4] bg-white" data-testid="tools-search" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {["todos", "disponible", "asignado", "dañado", "perdido"].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)} className={`px-3 py-2 rounded-xl text-xs font-semibold capitalize transition-all border ${statusFilter === s ? "bg-[#2E6DA4] text-white border-[#2E6DA4]" : "bg-white text-gray-600 border-gray-300 hover:border-[#2E6DA4]/40"}`} data-testid={`tool-filter-${s}`}>
              {s === "todos" ? "Todos" : TOOL_STATUS[s]?.label || s}
            </button>
          ))}
        </div>
        {/* Fix AX (2026-09-06): agrupar por obra o ver la lista completa. */}
        <div className="flex rounded-xl border border-gray-300 overflow-hidden flex-shrink-0" data-testid="tools-vista-switch">
          {[["obra", "Por obra"], ["lista", "Lista"]].map(([k, label]) => (
            <button key={k} onClick={() => setVista(k)}
              className={`px-3 py-2.5 text-xs font-semibold transition-all ${vista === k ? "bg-[#2E6DA4] text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
              data-testid={`tools-vista-${k}`}>
              {label}
            </button>
          ))}
        </div>
        <button onClick={onNew} className="flex items-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] active:scale-[0.98] text-white px-4 py-2.5 rounded-xl text-sm font-semibold transition-all shadow-sm whitespace-nowrap" data-testid="new-tool-btn">
          <Plus size={16} />Nueva Herramienta
        </button>
        <button onClick={onImportExcel} className="flex items-center gap-2 border-2 border-dashed border-emerald-300 text-emerald-700 hover:bg-emerald-50 active:scale-[0.98] px-4 py-2.5 rounded-xl text-sm font-semibold transition-all whitespace-nowrap" data-testid="import-tools-excel-btn">
          <FileSpreadsheet size={16} />Importar Excel (Plantilla)
        </button>
      </div>

      {/* Fix AX (2026-09-06): vista por obra — una columna por almacén, con su
          encabezado y las tarjetas debajo. La tarjeta es la misma de siempre. */}
      {vista === "obra" && (
        filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200/60 p-8 text-center">
            <Wrench size={32} className="text-gray-300 mx-auto mb-2" /><p className="text-gray-500 text-sm">No se encontraron herramientas</p>
          </div>
        ) : (
          <div className="flex gap-4 overflow-x-auto pb-2" data-testid="tools-por-obra">
            {(() => {
              const grupos = new Map();
              filtered.forEach(t => {
                const clave = t.current_almacen_nombre || "Almacén Central";
                if (!grupos.has(clave)) grupos.set(clave, []);
                grupos.get(clave).push(t);
              });
              const ordenadas = [...grupos.entries()].sort((a, b) => {
                const central = (s) => /almac[eé]n central/i.test(s) ? 0 : 1;
                return central(a[0]) - central(b[0]) || a[0].localeCompare(b[0]);
              });
              return ordenadas.map(([obra, lista]) => (
                <div key={obra} className="flex-shrink-0 w-[320px] space-y-3" data-testid={`tools-obra-col-${obra}`}>
                  <div className="bg-[#EBF3FB] border border-blue-100 rounded-xl px-3 py-2 sticky top-0 z-10">
                    <p className="text-sm font-bold text-[#1A4B7A] truncate" title={obra}>{obra}</p>
                    <p className="text-[11px] text-[#2E6DA4]">{lista.length} herramienta{lista.length !== 1 ? "s" : ""}</p>
                  </div>
                  {lista.map(t => <ToolCard key={t.id} t={t} onAssign={onAssign} onReturn={onReturn} onReport={onReport} onTransfer={onTransfer} onMaintenance={onMaintenance} onEdit={onEdit} />)}
                </div>
              ));
            })()}
          </div>
        )
      )}

      {/* Tool cards */}
      {vista === "lista" && (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tools-grid">
        {filtered.length === 0 ? (
          <div className="col-span-full bg-white rounded-xl border border-gray-200/60 p-8 text-center">
            <Wrench size={32} className="text-gray-300 mx-auto mb-2" /><p className="text-gray-500 text-sm">No se encontraron herramientas</p>
          </div>
        ) : filtered.map(t => {
          const statusInfo = TOOL_STATUS[t.status] || TOOL_STATUS.disponible;
          return (
            <div key={t.id} className={`bg-white rounded-xl border transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 ${t.overdue ? "border-red-300 shadow-red-100" : "border-gray-200/60"}`} data-testid="tool-card">
              {/* Overdue banner */}
              {t.overdue && (
                <div className="bg-red-50 border-b border-red-200 px-4 py-2 flex items-center gap-2 rounded-t-xl" data-testid="tool-overdue-banner">
                  <TriangleAlert size={13} className="text-red-500 flex-shrink-0" />
                  <span className="text-xs text-red-700 font-semibold">Sin devolver hace {t.hours_elapsed}h — Responsabilidad automática asignada</span>
                </div>
              )}
              <div className="p-4">
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div>
                    <span className="text-xs font-mono text-gray-400 font-semibold">{t.code}</span>
                    <h3 className="text-sm font-bold text-gray-900 mt-0.5 leading-tight">{t.name}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{t.category}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${statusInfo.cls}`} data-testid={`tool-status-${t.id}`}>{statusInfo.label}</span>
                </div>

                {t.value_pyg > 0 && <p className="text-xs text-gray-500 mb-2">Valor: <span className="font-semibold text-gray-700">{formatPYGCompact(t.value_pyg)}</span></p>}

                <div className="flex items-center gap-1.5 bg-gray-50 rounded-lg px-2.5 py-1.5 mb-2" data-testid={`tool-location-${t.id}`}>
                  <MapPin size={12} className="text-gray-400 flex-shrink-0" />
                  <span className="text-xs text-gray-600 font-medium truncate">{t.current_almacen_nombre || "Almacén Central"}</span>
                </div>

                {t.status === "asignado" && t.current_assignee_name && (
                  <div className="flex items-center gap-2 bg-blue-50 rounded-lg px-2.5 py-1.5 mb-3">
                    <User size={12} className="text-blue-500 flex-shrink-0" />
                    <span className="text-xs text-blue-700 font-medium truncate">{t.current_assignee_name}</span>
                  </div>
                )}
                {t.notes && <p className="text-xs text-gray-400 mb-3 truncate">{t.notes}</p>}

                {/* Action buttons */}
                <div className="flex gap-2 pt-3 border-t border-gray-100 flex-wrap">
                  {t.status === "disponible" && (
                    <button onClick={() => onAssign(t)} className="flex-1 bg-[#2E6DA4] hover:bg-[#245A8A] active:scale-[0.98] text-white rounded-lg py-2.5 text-xs font-semibold flex items-center justify-center gap-1.5 transition-all" data-testid={`assign-tool-btn-${t.id}`}>
                      <User size={13} />Asignar
                    </button>
                  )}
                  {t.status === "asignado" && (
                    <button onClick={() => onReturn(t)} className="flex-1 bg-emerald-600 hover:bg-emerald-700 active:scale-[0.98] text-white rounded-lg py-2.5 text-xs font-semibold flex items-center justify-center gap-1.5 transition-all" data-testid={`return-tool-btn-${t.id}`}>
                      <CheckCircle2 size={13} />Devolver
                    </button>
                  )}
                  {(t.status === "dañado" || t.status === "perdido") && (
                    <button onClick={() => onReturn(t)} className="flex-1 bg-gray-600 hover:bg-gray-700 active:scale-[0.98] text-white rounded-lg py-2.5 text-xs font-semibold flex items-center justify-center gap-1.5 transition-all" data-testid={`restore-tool-btn-${t.id}`}>
                      <RefreshCw size={13} />Reparada
                    </button>
                  )}
                  {(t.status === "disponible" || t.status === "asignado") && (
                    <button onClick={() => onReport(t)} className="flex-shrink-0 border border-red-200 text-red-500 hover:bg-red-50 rounded-lg py-2.5 px-3 text-xs font-semibold flex items-center gap-1.5 transition-all" data-testid={`report-tool-btn-${t.id}`}>
                      <ShieldAlert size={13} />Reportar
                    </button>
                  )}
                  <button onClick={() => onTransfer(t)} className="flex-shrink-0 border border-gray-200 text-gray-600 hover:bg-gray-50 rounded-lg py-2.5 px-3 text-xs font-semibold flex items-center gap-1.5 transition-all" data-testid={`transfer-tool-btn-${t.id}`}>
                    <ArrowRightLeft size={13} />Trasladar
                  </button>
                  <button onClick={() => onMaintenance(t)} className="flex-shrink-0 border border-gray-200 text-gray-600 hover:bg-gray-50 rounded-lg py-2.5 px-3 text-xs font-semibold flex items-center gap-1.5 transition-all" data-testid={`maintenance-tool-btn-${t.id}`}>
                    <Wrench size={13} />Mantenimiento
                  </button>
                  {onEdit && (
                    <button onClick={() => onEdit(t)} className="flex-shrink-0 border border-gray-200 text-gray-600 hover:bg-gray-50 rounded-lg py-2.5 px-3 text-xs font-semibold flex items-center gap-1.5 transition-all" data-testid={`edit-tool-btn-${t.id}`}>
                      <Pencil size={13} />Editar
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}

// Fix AX (2026-09-06): la misma tarjeta de siempre, extraída para poder usarla
// también en la vista agrupada por obra. No cambia ni un detalle de lo que
// muestra ni de sus botones: "esos cuadros con esos detalles estan bien"
// (Edson, 05/09) — misma marca, mismos íconos, mismo botón "Reparada" para
// herramientas dañadas/perdidas.
function ToolCard({ t, onAssign, onReturn, onReport, onTransfer, onMaintenance, onEdit }) {
  const statusInfo = TOOL_STATUS[t.status] || TOOL_STATUS.disponible;
  return (
    <div className={`bg-white rounded-xl border transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 ${t.overdue ? "border-red-300 shadow-red-100" : "border-gray-200/60"}`} data-testid="tool-card">
      {t.overdue && (
        <div className="bg-red-50 border-b border-red-200 px-4 py-2 flex items-center gap-2 rounded-t-xl" data-testid="tool-overdue-banner">
          <TriangleAlert size={13} className="text-red-500 flex-shrink-0" />
          <span className="text-xs text-red-700 font-semibold">Sin devolver hace {t.hours_elapsed}h — Responsabilidad automática asignada</span>
        </div>
      )}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div>
            <span className="text-xs font-mono text-gray-400 font-semibold">{t.code}</span>
            <h3 className="text-sm font-bold text-gray-900 mt-0.5 leading-tight">{t.name}</h3>
            <p className="text-xs text-gray-500 mt-0.5">{t.category}</p>
          </div>
          <span className={`px-2 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${statusInfo.cls}`} data-testid={`tool-status-${t.id}`}>{statusInfo.label}</span>
        </div>

        {t.value_pyg > 0 && <p className="text-xs text-gray-500 mb-2">Valor: <span className="font-semibold text-gray-700">{formatPYGCompact(t.value_pyg)}</span></p>}

        <div className="flex items-center gap-1.5 bg-gray-50 rounded-lg px-2.5 py-1.5 mb-2" data-testid={`tool-location-${t.id}`}>
          <MapPin size={12} className="text-gray-400 flex-shrink-0" />
          <span className="text-xs text-gray-600 font-medium truncate">{t.current_almacen_nombre || "Almacén Central"}</span>
        </div>

        {t.status === "asignado" && t.current_assignee_name && (
          <div className="flex items-center gap-2 bg-blue-50 rounded-lg px-2.5 py-1.5 mb-3">
            <User size={12} className="text-blue-500 flex-shrink-0" />
            <span className="text-xs text-blue-700 font-medium truncate">{t.current_assignee_name}</span>
          </div>
        )}
        {t.notes && <p className="text-xs text-gray-400 mb-3 truncate">{t.notes}</p>}

        <div className="flex gap-2 pt-3 border-t border-gray-100 flex-wrap">
          {t.status === "disponible" && (
            <button onClick={() => onAssign(t)} className="flex-1 bg-[#2E6DA4] hover:bg-[#245A8A] active:scale-[0.98] text-white rounded-lg py-2.5 text-xs font-semibold flex items-center justify-center gap-1.5 transition-all" data-testid={`assign-tool-btn-${t.id}`}>
              <User size={13} />Asignar
            </button>
          )}
          {t.status === "asignado" && (
            <button onClick={() => onReturn(t)} className="flex-1 bg-emerald-600 hover:bg-emerald-700 active:scale-[0.98] text-white rounded-lg py-2.5 text-xs font-semibold flex items-center justify-center gap-1.5 transition-all" data-testid={`return-tool-btn-${t.id}`}>
              <CheckCircle2 size={13} />Devolver
            </button>
          )}
          {(t.status === "dañado" || t.status === "perdido") && (
            <button onClick={() => onReturn(t)} className="flex-1 bg-gray-600 hover:bg-gray-700 active:scale-[0.98] text-white rounded-lg py-2.5 text-xs font-semibold flex items-center justify-center gap-1.5 transition-all" data-testid={`restore-tool-btn-${t.id}`}>
              <RefreshCw size={13} />Reparada
            </button>
          )}
          {(t.status === "disponible" || t.status === "asignado") && (
            <button onClick={() => onReport(t)} className="flex-shrink-0 border border-red-200 text-red-500 hover:bg-red-50 rounded-lg py-2.5 px-3 text-xs font-semibold flex items-center gap-1.5 transition-all" data-testid={`report-tool-btn-${t.id}`}>
              <ShieldAlert size={13} />Reportar
            </button>
          )}
          <button onClick={() => onTransfer(t)} className="flex-shrink-0 border border-gray-200 text-gray-600 hover:bg-gray-50 rounded-lg py-2.5 px-3 text-xs font-semibold flex items-center gap-1.5 transition-all" data-testid={`transfer-tool-btn-${t.id}`}>
            <ArrowRightLeft size={13} />Trasladar
          </button>
          <button onClick={() => onMaintenance(t)} className="flex-shrink-0 border border-gray-200 text-gray-600 hover:bg-gray-50 rounded-lg py-2.5 px-3 text-xs font-semibold flex items-center gap-1.5 transition-all" data-testid={`maintenance-tool-btn-${t.id}`}>
            <Wrench size={13} />Mantenimiento
          </button>
          {onEdit && (
            <button onClick={() => onEdit(t)} className="flex-shrink-0 border border-gray-200 text-gray-600 hover:bg-gray-50 rounded-lg py-2.5 px-3 text-xs font-semibold flex items-center gap-1.5 transition-all" data-testid={`edit-tool-btn-${t.id}`}>
              <Pencil size={13} />Editar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── CONTINGENCIA TAB ─────────────────────────────────────
function ContingenciaTab({ contingency, loading }) {
  const critical = contingency.filter(m => m.pct <= 50);
  const totalSuggestedCost = contingency.reduce((sum, m) => sum + (m.suggested_cost_pyg || 0), 0);

  if (loading) return <div className="space-y-3">{[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>;

  return (
    <div className="space-y-4" data-testid="contingencia-tab">
      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3" data-testid="contingency-critical-count">
          <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center"><AlertTriangle size={20} className="text-red-600" /></div>
          <div><p className="text-xs font-semibold text-red-500 uppercase tracking-wide">Críticos</p><p className="text-2xl font-bold text-red-700">{critical.length}</p></div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center"><TrendingDown size={20} className="text-amber-600" /></div>
          <div><p className="text-xs font-semibold text-amber-500 uppercase tracking-wide">Stock Bajo</p><p className="text-2xl font-bold text-amber-700">{contingency.filter(m => m.pct > 50 && m.pct <= 100).length}</p></div>
        </div>
        <div className="bg-[#EBF2F9] border border-[#2E6DA4]/30 rounded-xl p-4 flex items-center gap-3" data-testid="contingency-total-cost">
          <div className="w-10 h-10 rounded-xl bg-[#2E6DA4]/10 flex items-center justify-center"><Zap size={20} className="text-[#2E6DA4]" /></div>
          <div><p className="text-xs font-semibold text-[#2E6DA4] uppercase tracking-wide">Costo Reposición Est.</p><p className="text-lg font-bold text-[#2E6DA4]">{formatPYGCompact(totalSuggestedCost)}</p></div>
        </div>
      </div>

      {/* Contingency list */}
      <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm overflow-hidden" data-testid="contingency-list">
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/80"><p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Todos los Materiales — Ordenados por Nivel de Stock</p></div>
        {contingency.length === 0 ? (
          <div className="p-8 text-center"><Package size={32} className="text-gray-300 mx-auto mb-2" /><p className="text-sm text-gray-500">No hay materiales registrados</p></div>
        ) : (
          <div className="divide-y divide-gray-100">
            {contingency.map(m => {
              const clr = stockColor(m.pct);
              return (
                <div key={m.id} className="px-4 py-3 flex items-center gap-4" data-testid="contingency-row">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-mono text-gray-400">{m.code}</span>
                      <span className="text-sm font-semibold text-gray-900 truncate">{m.name}</span>
                      {m.marca && <span className="text-xs text-gray-400 flex-shrink-0" data-testid={`contingency-marca-${m.id}`}>· {m.marca}</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-100 rounded-full h-2 max-w-[180px]">
                        <div className={`h-2 rounded-full transition-all duration-700 ${clr.bar}`} style={{ width: `${Math.min(100, m.pct)}%` }} />
                      </div>
                      <span className={`text-xs font-semibold ${clr.text}`}>{m.total_qty} / {m.min_stock} {m.unit}</span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    {m.suggested_qty > 0 ? (
                      <div>
                        <p className="text-xs font-semibold text-gray-700">Reponer: +{m.suggested_qty} {m.unit}</p>
                        <p className="text-xs text-gray-400">{formatPYGCompact(m.suggested_cost_pyg)}</p>
                      </div>
                    ) : (
                      <span className="text-xs text-emerald-600 font-semibold">Stock OK</span>
                    )}
                  </div>
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${clr.bar}`} />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────
export default function Inventory({ defaultTab = "materiales" }) {
  const { user } = useAuth();
  // Fix FH (2026-10-08): el Residente de Obra entra solo a Alquileres (su menú solo lleva ahí).
  const soloAlquileres = user?.role === "Residente de Obra";
  // Fix IC (2026-10-29): el Residente también entra a registrar el consumo del día de su depósito.
  const visibleTabs = TABS.filter(t => (soloAlquileres ? (t.id === "alquileres" || t.id === "almacenes") : (t.id !== "alquileres" || roleAllowed(user?.role, ALQUILER_TAB_ROLES))));
  const [activeTab, setActiveTab] = useState(
    soloAlquileres ? "alquileres"
      : defaultTab === "alquileres" && !roleAllowed(user?.role, ALQUILER_TAB_ROLES) ? "materiales" : defaultTab
  );
  const [materials, setMaterials] = useState([]);
  const [movements, setMovements] = useState([]);
  const [tools, setTools] = useState([]);
  const [contingency, setContingency] = useState([]);
  const [projects, setProjects] = useState([]);
  const [almacenes, setAlmacenes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState({ materials: true, movements: true, tools: true, contingency: true });

  // Modals
  const [materialModal, setMaterialModal] = useState({ open: false, item: null });
  const [importMaterialsModal, setImportMaterialsModal] = useState(false);
  const [importExcelModal, setImportExcelModal] = useState(null); // "materials" | "tools" | null
  const [entryModal, setEntryModal] = useState(false);
  const [transferModal, setTransferModal] = useState(false);
  const [entryOCModal, setEntryOCModal] = useState(false);
  const [consumoModal, setConsumoModal] = useState(false);
  const [toolModal, setToolModal] = useState({ open: false, item: null });
  const [assignModal, setAssignModal] = useState({ open: false, tool: null });
  const [returnModal, setReturnModal] = useState({ open: false, tool: null });
  const [reportModal, setReportModal] = useState({ open: false, tool: null });
  const [toolTransferModal, setToolTransferModal] = useState({ open: false, tool: null });
  const [toolMaintenanceModal, setToolMaintenanceModal] = useState({ open: false, tool: null });

  const fetchAll = useCallback(async () => {
    // Fix FH (2026-10-08): el Residente solo ve Alquileres (la pestaña trae sus propios datos).
    if (soloAlquileres) { setLoading({ materials: false, movements: false, tools: false, contingency: false }); return; }
    try {
      const [matRes, movRes, toolRes, conRes, projRes, statRes, almRes] = await Promise.all([
        axios.get(`${API}/inventory/materials`, { withCredentials: true }),
        axios.get(`${API}/inventory/movements`, { withCredentials: true }),
        axios.get(`${API}/inventory/tools`, { withCredentials: true }),
        axios.get(`${API}/inventory/contingency`, { withCredentials: true }),
        axios.get(`${API}/projects`, { withCredentials: true }),
        axios.get(`${API}/inventory/stats`, { withCredentials: true }),
        axios.get(`${API}/inventory/almacenes`, { withCredentials: true }),
      ]);
      setMaterials(matRes.data);
      setMovements(movRes.data);
      setTools(toolRes.data);
      setContingency(conRes.data);
      setProjects(projRes.data);
      setStats(statRes.data);
      setAlmacenes(almRes.data);
    } catch (e) {
      toast.error("Error al cargar inventario");
    } finally {
      setLoading({ materials: false, movements: false, tools: false, contingency: false });
    }
  }, [soloAlquileres]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const refresh = useCallback(() => {
    setLoading({ materials: true, movements: true, tools: true, contingency: true });
    fetchAll();
  }, [fetchAll]);

  const closeMaterialModal = useCallback(() => { setMaterialModal({ open: false, item: null }); }, []);
  const onMaterialSaved = useCallback(() => { closeMaterialModal(); refresh(); }, [closeMaterialModal, refresh]);
  const closeToolModal = useCallback(() => { setToolModal({ open: false, item: null }); }, []);
  const onToolSaved = useCallback(() => { closeToolModal(); refresh(); }, [closeToolModal, refresh]);

  const lowStockCount = stats?.low_stock_count || 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 min-h-full" data-testid="inventory-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">Inventario</h1>
          <p className="text-sm text-gray-500 mt-0.5">Control de materiales, herramientas y stock de contingencia</p>
        </div>
        <button onClick={refresh} className="p-2.5 rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50 transition-all" data-testid="inventory-refresh-btn" title="Actualizar">
          <RefreshCw size={18} />
        </button>
      </div>

      {/* Stats bar */}
      {!soloAlquileres && (   /* Fix FH (2026-10-08) */
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="inventory-stats">
        {[
          { label: "Materiales", value: stats?.total_materials ?? "—", icon: Package, color: "text-[#2E6DA4]", bg: "bg-[#EBF2F9]" },
          { label: "Stock Bajo", value: lowStockCount, icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-50", alert: lowStockCount > 0 },
          { label: "Valor Inventario", value: formatPYGCompact(stats?.total_inventory_value_pyg || 0), icon: BarChart3, color: "text-emerald-600", bg: "bg-emerald-50" },
          { label: "Herramientas", value: stats ? `${stats.tools_disponible}/${stats.tools_total}` : "—", icon: Wrench, color: "text-purple-600", bg: "bg-purple-50" },
        ].map(s => (
          <div key={s.label} className={`bg-white rounded-xl border ${s.alert ? "border-amber-200" : "border-gray-200/60"} p-4 flex items-center gap-3 shadow-sm hover:shadow-md transition-shadow`} data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className={`w-10 h-10 rounded-xl ${s.bg} flex items-center justify-center flex-shrink-0`}>
              <s.icon size={20} className={s.color} />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide truncate">{s.label}</p>
              <p className={`text-lg font-bold ${s.alert ? "text-amber-700" : "text-gray-900"} tabular-nums`}>{s.value}</p>
            </div>
          </div>
        ))}
      </div>
      )}

      {/* Tab bar */}
      <nav className="flex gap-1 overflow-x-auto pb-1" data-testid="inventory-tabs">
        {visibleTabs.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          const showBadge = tab.id === "contingencia" && lowStockCount > 0;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all whitespace-nowrap relative ${isActive ? "bg-[#2E6DA4] text-white shadow-sm" : "text-gray-500 hover:text-gray-800 hover:bg-gray-100"}`}
              data-testid={`inventory-tab-${tab.id}`}>
              <Icon size={16} />{tab.label}
              {showBadge && <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-[9px] text-white flex items-center justify-center font-bold">{lowStockCount}</span>}
            </button>
          );
        })}
      </nav>

      {/* Tab content */}
      <div className="animate-in fade-in duration-200">
        {activeTab === "materiales" && (
          <MaterialesTab materials={materials} almacenes={almacenes} loading={loading.materials}
            onNew={() => setMaterialModal({ open: true, item: null })}
            onImport={() => setImportMaterialsModal(true)}
            onImportExcel={() => setImportExcelModal("materials")}
            onEdit={m => setMaterialModal({ open: true, item: m })}
            onRefresh={refresh} />
        )}
        {activeTab === "movimientos" && (
          <MovimientosTab movements={movements} materials={materials} projects={projects} loading={loading.movements}
            onNewEntry={() => setEntryModal(true)}
            onNewTransfer={() => setTransferModal(true)}
            onNewEntryOC={() => setEntryOCModal(true)}
            onNewConsumo={() => setConsumoModal(true)}
            onRefresh={refresh} />
        )}
        {activeTab === "movimiento_inventario" && <MovimientoInventarioTab />}
        {activeTab === "almacenes" && <AlmacenesDepositosTab materials={materials} onRefresh={refresh} />}
        {activeTab === "herramientas" && (
          <HerramientasTab tools={tools} loading={loading.tools}
            onNew={() => setToolModal({ open: true, item: null })}
            onImportExcel={() => setImportExcelModal("tools")}
            onAssign={t => setAssignModal({ open: true, tool: t })}
            onReturn={t => setReturnModal({ open: true, tool: t })}
            onReport={t => setReportModal({ open: true, tool: t })}
            onTransfer={t => setToolTransferModal({ open: true, tool: t })}
            onMaintenance={t => setToolMaintenanceModal({ open: true, tool: t })}
            onEdit={roleAllowed(user?.role, TOOL_EDIT_ROLES) ? (t => setToolModal({ open: true, item: t })) : null}
            onRefresh={refresh} />
        )}
        {activeTab === "contingencia" && (
          <ContingenciaTab contingency={contingency} loading={loading.contingency} />
        )}
        {activeTab === "alquileres" && <AlquileresTab />}
        {activeTab === "tareas" && <TareasPanel modulo="inventario" titulo="Tareas y Flujo de Trabajo — Inventario" />}
      </div>

      {/* Modals */}
      <MaterialModal open={materialModal.open} onClose={closeMaterialModal} onSaved={onMaterialSaved} material={materialModal.item} />
      <ImportMaterialsModal open={importMaterialsModal} onClose={() => setImportMaterialsModal(false)} onConfirmed={() => { setImportMaterialsModal(false); refresh(); }} projects={projects} />
      <ImportExcelTemplateModal type={importExcelModal} open={!!importExcelModal} onClose={() => setImportExcelModal(null)} onConfirmed={refresh} />
      <EntryModal open={entryModal} onClose={() => setEntryModal(false)} onSaved={() => { setEntryModal(false); refresh(); }} materials={materials} almacenes={almacenes} />
      <TransferModal open={transferModal} onClose={() => setTransferModal(false)} onSaved={() => { setTransferModal(false); refresh(); }} materials={materials} almacenes={almacenes} />
      <EntryFromOCModal open={entryOCModal} onClose={() => setEntryOCModal(false)} onSaved={() => { setEntryOCModal(false); refresh(); }} almacenes={almacenes} materials={materials} />
      <ConsumoModal open={consumoModal} onClose={() => setConsumoModal(false)} onSaved={() => { setConsumoModal(false); refresh(); }} materials={materials} almacenes={almacenes} />
      <ToolModal open={toolModal.open} onClose={closeToolModal} onSaved={onToolSaved} tool={toolModal.item} />
      <AssignModal open={assignModal.open} onClose={() => setAssignModal({ open: false, tool: null })} onSaved={() => { setAssignModal({ open: false, tool: null }); refresh(); }} tool={assignModal.tool} />
      <ReturnModal open={returnModal.open} onClose={() => setReturnModal({ open: false, tool: null })} onSaved={() => { setReturnModal({ open: false, tool: null }); refresh(); }} tool={returnModal.tool} />
      <ReportModal open={reportModal.open} onClose={() => setReportModal({ open: false, tool: null })} onSaved={() => { setReportModal({ open: false, tool: null }); refresh(); }} tool={reportModal.tool} />
      <ToolTransferLocationModal open={toolTransferModal.open} onClose={() => setToolTransferModal({ open: false, tool: null })} onSaved={() => { setToolTransferModal({ open: false, tool: null }); refresh(); }} tool={toolTransferModal.tool} almacenes={almacenes} />
      <ToolMaintenanceModal open={toolMaintenanceModal.open} onClose={() => setToolMaintenanceModal({ open: false, tool: null })} tool={toolMaintenanceModal.tool} />
    </div>
  );
}
